
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import CollapsibleSection from '../components/CollapsibleSection';
import ClientIntakeForm from '../components/ClientIntakeForm';
import VideoSessionWhereby from '../components/VideoSessionWhereby';
import LocationConfirmationModal from '../components/LocationConfirmationModal';
import FloatingHopeButton from '../components/FloatingHopeButton';
import { HopeVoiceService } from '../services/HopeVoiceService';
import FormViewer from '../components/FormViewer';
import EmergencyCancellationForm from '../components/client/EmergencyCancellationForm';
import FirebaseActivityService from '../services/firebase/FirebaseActivityService';
import { LogActivityRequest } from '../types/activity';
import ClientMessagesTab from '../components/dashboard/client/MessagesTab';
import { TeamMember } from '../services/TeamMemberService';
import { useToast } from '../components/Toast';
import { FirebasePatientService, Patient } from '../services/firebase/FirebasePatientService';
import { db } from '../config/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { firebaseAuthService } from '../services/firebase/FirebaseAuthService';
import FormAssignmentService, { AssignedForm, FormTemplate } from '../services/FormAssignmentService';
import { FirebaseFormResponseService, FormResponse } from '../services/firebase/FirebaseFormResponseService';
import { firebaseAppointmentService, Appointment } from '../services/firebase/FirebaseAppointmentService';
import { formatTime12Hour } from '../utils/timeFormat';
import { useMessaging } from '../contexts/MessagingContext';
import FirebaseMessagingService from '../services/firebase/FirebaseMessagingService';
import { FirebaseHomeworkService } from '../services/firebase/FirebaseHomeworkService';
import { HomeworkAssignment } from '../types/client';
import {
  FirebaseDiagnosisPrescriptionService,
  FirebaseDiagnosisRecord,
  FirebasePrescriptionRecord
} from '../services/firebase/FirebaseDiagnosisPrescriptionService';
import {
  FirebaseTreatmentPlanService,
  TreatmentPlan
} from '../services/firebase/FirebaseTreatmentPlanService';

// Pharmacy interface and data (same as in ClientIntakeForm)
interface Pharmacy {
  id: string;
  name: string;
  address: string;
  phone: string;
  hours: string;
  acceptsInsurance: string[];
  zipCode: string;
  serviceZipCodes: string[];
  distanceFromClient?: number;
}

const availablePharmacies: Pharmacy[] = [
  {
    id: '1',
    name: 'CVS Pharmacy - Downtown Atlanta',
    address: 'Downtown Atlanta, GA 30309',
    phone: 'Contact pharmacy directly',
    hours: 'Mon-Sat: 8AM-10PM, Sun: 9AM-9PM',
    acceptsInsurance: ['Blue Cross Blue Shield', 'Aetna', 'Cigna', 'UnitedHealthcare'],
    zipCode: '30309',
    serviceZipCodes: ['30309', '30308', '30313', '30314', '30318']
  },
  {
    id: '2',
    name: 'Walgreens - Midtown Atlanta',
    address: 'Midtown Atlanta, GA 30309',
    phone: 'Contact pharmacy directly',
    hours: 'Mon-Fri: 7AM-11PM, Sat-Sun: 8AM-10PM',
    acceptsInsurance: ['Blue Cross Blue Shield', 'Anthem', 'Humana', 'Cigna'],
    zipCode: '30309',
    serviceZipCodes: ['30309', '30308', '30305', '30306', '30312']
  },
  {
    id: '3',
    name: 'Rite Aid - Virginia Highland',
    address: 'Virginia Highland, Atlanta, GA 30306',
    phone: 'Contact pharmacy directly',
    hours: 'Mon-Sun: 8AM-9PM',
    acceptsInsurance: ['UnitedHealthcare', 'Aetna', 'Blue Cross Blue Shield'],
    zipCode: '30306',
    serviceZipCodes: ['30306', '30307', '30309', '30324']
  },
  {
    id: '4',
    name: 'Kroger Pharmacy - Buckhead',
    address: 'Buckhead, Atlanta, GA 30305',
    phone: 'Contact pharmacy directly',
    hours: 'Mon-Sun: 9AM-9PM',
    acceptsInsurance: ['Blue Cross Blue Shield', 'Cigna', 'Anthem', 'Humana'],
    zipCode: '30305',
    serviceZipCodes: ['30305', '30327', '30342', '30319', '30309']
  },
  {
    id: '5',
    name: 'Publix Pharmacy - Decatur',
    address: 'Decatur, GA 30033',
    phone: 'Contact pharmacy directly',
    hours: 'Mon-Sat: 9AM-9PM, Sun: 11AM-6PM',
    acceptsInsurance: ['Blue Cross Blue Shield', 'UnitedHealthcare', 'Aetna', 'Cigna'],
    zipCode: '30033',
    serviceZipCodes: ['30033', '30030', '30032', '30307', '30316']
  },
  {
    id: '6',
    name: 'Independent Community Pharmacy',
    address: 'East Atlanta, GA 30312',
    phone: 'Contact pharmacy directly',
    hours: 'Mon-Fri: 9AM-7PM, Sat: 9AM-5PM',
    acceptsInsurance: ['Blue Cross Blue Shield', 'Aetna', 'Local Plans'],
    zipCode: '30312',
    serviceZipCodes: ['30312', '30315', '30316', '30309']
  }
];

export interface ClientDashboardProps {}

const ClientDashboard: React.FC<ClientDashboardProps> = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const toast = useToast();
  // For phone OTP users, patientId differs from user.uid — use the linked patient record
  const resolvedPatientId = userProfile?.patientId || user?.uid;
  const clientFirstName = user?.displayName?.split(' ')[0] || userProfile?.displayName?.split(' ')[0] || 'there';
  const timeGreeting = new Date().getHours() < 12 ? 'Good morning' : new Date().getHours() < 17 ? 'Good afternoon' : 'Good evening';

  // Helper: Format snake_case or raw insurance values to readable Title Case
  const formatInsurance = (val?: string) => {
    if (!val) return 'N/A';
    return val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Helper: Firestore stores appointment dates as UTC midnight. Local operations (setHours,
  // toLocaleDateString) shift the date by -1 day for western timezones. This converts
  // UTC date components to local midnight so date operations work correctly.
  const toLocalDate = (d: Date): Date =>
    new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

  // Helper: Build a Date representing the appointment time in the practice timezone (Eastern).
  // startTime ("14:00") is stored as Eastern local time. Using setHours() would apply the
  // CLIENT device's timezone instead, causing a 1-hr mismatch for clients in CDT vs EDT.
  // This Intl-based trick resolves the offset for America/New_York automatically (handles DST).
  const parseApptDateTime = (appointmentDate: Date, startTime: string): Date => {
    const base = toLocalDate(appointmentDate);
    const [h, m] = startTime.split(':').map(Number);
    const naive = new Date(base.getFullYear(), base.getMonth(), base.getDate(), h, m, 0);
    const etFormatted = naive.toLocaleString('en-US', { timeZone: 'America/New_York' });
    const etAsLocal = new Date(etFormatted);
    const offset = naive.getTime() - etAsLocal.getTime();
    return new Date(naive.getTime() + offset);
  };

  // Hope greets the client on dashboard load (once per session)
  useEffect(() => {
    if (!HopeVoiceService.getSettings().enabled) return;
    const greetedKey = `client_greeted_${new Date().toDateString()}`;
    if (sessionStorage.getItem(greetedKey)) return;

    const timer = setTimeout(() => {
      const hour = new Date().getHours();
      const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
      HopeVoiceService.speak(
        `${greeting}, ${clientFirstName}. Welcome to your client portal. You can navigate using the tabs below, or ask me to read anything on screen.`,
        'polite'
      );
      sessionStorage.setItem(greetedKey, 'true');
    }, 1500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientFirstName]);

  // Self-healing: if phone OTP user's profile is missing patientId, re-link via lookup API
  useEffect(() => {
    const relinkIfNeeded = async () => {
      if (!user || !userProfile) return;
      // If patientId is already set, nothing to do
      if (userProfile.patientId) return;
      // Works for phone-OTP users and for verified-email accounts; the server
      // decides which identity it can match on.
      if (!user.phoneNumber && !user.email) return;

      try {
        const token = await user.getIdToken();
        // The endpoint reads the phone/email off the verified token and writes
        // users/{uid}.patientId itself. The client cannot write that field —
        // it is an authorization field, and letting the client set it allowed
        // any account to link itself to any patient's chart.
        const response = await fetch('/api/lookup-patient-by-phone', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (data.found && data.linked) {
          // Refresh the auth context so resolvedPatientId picks up the link
          window.location.reload();
        }
      } catch (err) {
        console.warn('Patient re-link attempt failed (non-critical):', err);
      }
    };
    relinkIfNeeded();
  }, [user, userProfile]);

  // Section state: all menu pages
  type Section = 'overview' | 'updateProfile' | 'updatePharmacy' | 'providerInfo' | 'diagnosisInfo' | 'myPrescriptions' | 'resources' | 'treatmentPlan' | 'homework' | 'viewHistory' | 'getHelp' | 'formsToComplete' | 'completedForms' | 'clientInfo' | 'messages';
  const [activeSection, setActiveSection] = useState<Section>('overview');

  // Get unread message count from messaging context
  const { conversations } = useMessaging();
  const unreadCount = conversations.reduce((total, conv) => total + (conv.unreadCount?.client || 0), 0);

  // Sub-section state for Client Info (which includes Treatment Plan)
  type ClientInfoSubSection = 'overview' | 'treatmentPlan';
  const [activeClientInfoSubSection, setActiveClientInfoSubSection] = useState<ClientInfoSubSection>('overview');

  // Sub-section state for Treatment Plan (under Client Info)
  type TreatmentSubSection = 'overview' | 'diagnosisInfo' | 'myPrescriptions' | 'homework';
  const [activeTreatmentSubSection, setActiveTreatmentSubSection] = useState<TreatmentSubSection>('overview');

  // Video modal state
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Welcome modal state - Check Firestore profile first, fall back to localStorage
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  useEffect(() => {
    if (userProfile?.hasSeenWelcome) return; // Already seen per Firestore
    const localSeen = localStorage.getItem('hasSeenClientWelcome');
    if (localSeen) return; // Already seen per localStorage
    setShowWelcomeModal(true);
  }, [userProfile]);

  // Active video session state
  const [activeVideoSession, setActiveVideoSession] = useState<Appointment | null>(null);

  // Location confirmation modal state
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [pendingVideoAppointment, setPendingVideoAppointment] = useState<Appointment | null>(null);

  // Appointment management state
  const [showAllAppointments, setShowAllAppointments] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<Appointment | null>(null);
  const [appointmentToReschedule, setAppointmentToReschedule] = useState<Appointment | null>(null);
  const [showCancelSuccess, setShowCancelSuccess] = useState(false);
  const [emergencyCancellationAppointment, setEmergencyCancellationAppointment] = useState<Appointment | null>(null);
  const [clientAppointments, setClientAppointments] = useState<Appointment[]>([]);
  // Effective patient ID — updated when email-based patient link is discovered
  const [effectivePatientId, setEffectivePatientId] = useState<string | undefined>(resolvedPatientId);

  // Current selected pharmacy state - loaded from patient's Firestore record
  const [currentPharmacy, setCurrentPharmacy] = useState<Pharmacy | undefined>(undefined);

  // Provider data from Firestore
  const [provider, setProvider] = useState<TeamMember | null>(null);
  const [practiceInfo, setPracticeInfo] = useState<{
    practiceName?: string;
    phone?: string;
    email?: string;
    website?: string;
    officeAddress?: { street?: string; city?: string; state?: string; zipCode?: string };
  }>({});
  const [currentPatient, setCurrentPatient] = useState<Patient | null>(null);

  // Activity logging helper — requires provider to be loaded
  const logClientActivity = useCallback((
    activityType: string,
    title: string,
    description: string,
    metadata?: Record<string, any>
  ) => {
    if (!provider || !resolvedPatientId) return;
    const clientName = user?.displayName || userProfile?.displayName || 'Client';
    FirebaseActivityService.getInstance().logActivity({
      providerId: provider.id,
      clientId: resolvedPatientId,
      clientName,
      activityType,
      title,
      description,
      metadata
    } as LogActivityRequest).catch(err => console.warn('[Activity] Log failed:', err));
  }, [provider, resolvedPatientId, user?.displayName, userProfile?.displayName]);

  // Log client login activity (once per day per session)
  useEffect(() => {
    if (!provider || !resolvedPatientId) return;
    const loginKey = `client_login_logged_${new Date().toDateString()}`;
    if (sessionStorage.getItem(loginKey)) return;
    logClientActivity('login', 'Client logged in', `${user?.displayName || 'Client'} logged into the portal.`);
    sessionStorage.setItem(loginKey, 'true');
  }, [provider, resolvedPatientId, logClientActivity, user?.displayName]);

  // Form assignments
  const [assignedForms, setAssignedForms] = useState<AssignedForm[]>([]);
  const [formTemplates, setFormTemplates] = useState<FormTemplate[]>([]);
  const [activeFormAssignment, setActiveFormAssignment] = useState<AssignedForm | null>(null);
  const [canBookAppointment, setCanBookAppointment] = useState<boolean>(false);
  const formService = FormAssignmentService.getInstance();

  // Completed form responses from Firestore
  const [completedFormResponses, setCompletedFormResponses] = useState<FormResponse[]>([]);
  const [loadingCompletedForms, setLoadingCompletedForms] = useState(false);

  // Homework assignments from Firestore
  const [clientHomework, setClientHomework] = useState([] as HomeworkAssignment[]);
  const [completingHomeworkId, setCompletingHomeworkId] = useState('');
  const [showCompletedHomework, setShowCompletedHomework] = useState(false);
  const [homeworkNotes, setHomeworkNotes] = useState('');

  // Diagnosis and prescription data from Firestore
  const [clientDiagnoses, setClientDiagnoses] = useState<FirebaseDiagnosisRecord[]>([]);
  const [clientPrescriptions, setClientPrescriptions] = useState<FirebasePrescriptionRecord[]>([]);

  // Treatment plans from Firestore
  const [clientTreatmentPlans, setClientTreatmentPlans] = useState<TreatmentPlan[]>([]);

  useEffect(() => {
    const loadPatientData = async () => {
      // Use linked patientId for phone OTP users, otherwise use Firebase Auth UID
      const patientId = resolvedPatientId;
      if (!patientId) return;

      let patient = await FirebasePatientService.getPatient(patientId);

      // Fallback: if no patient found by UID, search by email and link the account
      if (!patient && user?.email) {
        const emailPatient = await FirebasePatientService.getPatientByEmail(user.email);
        if (emailPatient) {
          console.log('[ClientDashboard] Matched patient by email for auth UID:', user.uid);
          // Persist the link server-side. The client cannot write patientId —
          // the endpoint re-derives the match from the verified token, so a
          // client-side match here is only a display shortcut, never the
          // grant of access.
          try {
            const token = await user.getIdToken();
            await fetch('/api/lookup-patient-by-phone', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
            });
          } catch (err) {
            console.warn('Could not persist patient link (non-critical):', err);
          }
          patient = emailPatient;
          // Update effective patient ID so appointment subscription uses the correct ID
          setEffectivePatientId(emailPatient.id);
        }
      }

      setCurrentPatient(patient || null);

      // Load preferred pharmacy from patient record if saved
      if ((patient as any)?.preferredPharmacy) {
        setCurrentPharmacy((patient as any).preferredPharmacy);
      }

      // Get the assigned provider from Firestore providers collection
      // Fallback: if no assignedProviderId, check the client's appointments for a providerId
      let provId = patient?.assignedProviderId;
      if (!provId && patient) {
        try {
          const appts = await firebaseAppointmentService.getAppointments({ patientId: patient.id });
          if (appts.length > 0) {
            provId = appts[0].providerId;
            // Save it back so future loads are instant
            await FirebasePatientService.updatePatient(patient.id, { assignedProviderId: provId } as any).catch(() => {});
          }
        } catch { /* silent */ }
      }
      if (provId) {
        try {
          const providerDoc = await getDoc(doc(db, 'providers', provId));
          if (providerDoc.exists()) {
            const data = providerDoc.data();
            const providerName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Provider';
            setProvider({
              id: providerDoc.id,
              name: providerName,
              email: data.email || '',
              role: data.licenseType || 'Provider',
              twoFactor: false,
              lastSignIn: '',
              education: data.educationBackground ? [data.educationBackground] : [],
              certifications: data.licenses?.map((l: any) => `${l.type} - ${l.state} (${l.licenseNumber})`) || [],
              specialties: data.specializations || data.specialties || [],
              availability: '',
              languages: data.languages || [],
              phone: data.phone || '',
              bio: data.bio || '',
              photoUrl: data.profilePhoto || '',
              videoUrl: data.introductionVideo || '',
              location: data.practiceName ? `${data.practiceName}${data.officeAddress ? ', ' + data.officeAddress : ''}` : '',
              insuranceAccepted: data.insuranceAccepted || [],
            });
            setPracticeInfo({
              practiceName: data.practiceName || '',
              phone: data.phone || '',
              email: data.email || '',
              website: data.website || '',
              officeAddress: data.officeAddress || undefined,
            });

            // After transfer: reassign existing conversations to the current provider
            if (patient) {
              FirebaseMessagingService.getInstance().reassignConversationsToProvider(
                patient.id,
                providerDoc.id,
                providerName
              ).catch(() => {});
            }
          }
        } catch (err) {
          console.error('Failed to load provider from Firestore:', err);
        }
      }

      // Load form assignments for the current patient
      if (patient) {
        try {
          const forms = await formService.getAssignmentsByClientId(patient.id);
          setAssignedForms(forms);
          setFormTemplates(formService.getAllFormTemplates());
          const bookable = await formService.canClientBookAppointment(patient.id);
          setCanBookAppointment(bookable);
        } catch (err) {
          console.error('Failed to load form assignments:', err);
        }
      }
    };

    loadPatientData();

    // Listen for keyboard shortcut navigation events
    const handleNavigateToSection = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { section } = customEvent.detail;
      if (section) {
        setActiveSection(section);
      }
    };
    window.addEventListener('navigate-to-section', handleNavigateToSection as EventListener);

    // Real-time subscription for form assignment updates
    const patientId = resolvedPatientId;
    let unsubscribeForms: (() => void) | undefined;
    let unsubscribeHomework: (() => void) | undefined;
    let unsubscribeDiagnoses: (() => void) | undefined;
    let unsubscribePrescriptions: (() => void) | undefined;
    let unsubscribeTreatmentPlans: (() => void) | undefined;
    if (patientId) {
      unsubscribeForms = formService.subscribeToClientAssignments(patientId, (updatedAssignments) => {
        setAssignedForms(updatedAssignments);
        // Refresh booking eligibility when assignments change
        formService.canClientBookAppointment(patientId).then(setCanBookAppointment).catch(() => {});
      });
      unsubscribeHomework = FirebaseHomeworkService.subscribeToClientHomework(patientId, (items) => {
        setClientHomework(items);
      });
      unsubscribeDiagnoses = FirebaseDiagnosisPrescriptionService.subscribeToClientDiagnoses(patientId, (data) => {
        setClientDiagnoses(data);
      });
      unsubscribePrescriptions = FirebaseDiagnosisPrescriptionService.subscribeToClientPrescriptions(patientId, (data) => {
        setClientPrescriptions(data);
      });
      unsubscribeTreatmentPlans = FirebaseTreatmentPlanService.subscribeToPatientPlans(patientId, (data) => {
        setClientTreatmentPlans(data);
      });
    }

    return () => {
      window.removeEventListener('navigate-to-section', handleNavigateToSection as EventListener);
      if (unsubscribeForms) unsubscribeForms();
      if (unsubscribeHomework) unsubscribeHomework();
      if (unsubscribeDiagnoses) unsubscribeDiagnoses();
      if (unsubscribePrescriptions) unsubscribePrescriptions();
      if (unsubscribeTreatmentPlans) unsubscribeTreatmentPlans();
    };
  }, [resolvedPatientId]);

  // Load completed form responses from Firestore when section is shown
  useEffect(() => {
    if (activeSection === 'completedForms' && resolvedPatientId) {
      setLoadingCompletedForms(true);
      FirebaseFormResponseService.getResponsesByClientSelf(resolvedPatientId)
        .then(responses => setCompletedFormResponses(responses))
        .catch(err => console.error('Failed to load completed forms:', err))
        .finally(() => setLoadingCompletedForms(false));
    }
  }, [activeSection, resolvedPatientId]);

  // Load appointments from Firestore (with fallback for phone OTP permission issues)
  useEffect(() => {
    const apptPatientId = effectivePatientId || resolvedPatientId;
    if (!apptPatientId) return;

    let unsubscribe: (() => void) | null = null;

    // Try real-time subscription first
    try {
      unsubscribe = firebaseAppointmentService.subscribeToAppointments(
        { patientId: apptPatientId },
        (appts) => setClientAppointments(appts)
      );
    } catch (err) {
      console.error('Subscription setup failed, trying direct fetch:', err);
    }

    // Also do a direct fetch as fallback (handles cases where onSnapshot silently fails)
    // Use couples-aware query so patient2 sees couples appointments too
    firebaseAppointmentService.getPatientAppointmentsIncludingCouples(apptPatientId)
      .then(appts => {
        if (appts.length > 0) {
          setClientAppointments(appts);
        }
      })
      .catch(err => {
        console.error('Direct appointment fetch also failed:', err);
        // If both fail, the patientId might not match — log for debugging
        console.warn('resolvedPatientId used for query:', resolvedPatientId, 'user.uid:', user?.uid);
      });

    return () => { if (unsubscribe) unsubscribe(); };
  }, [resolvedPatientId, effectivePatientId]);

  // Listen for messages from video session popup window
  useEffect(() => {
    const handlePopupMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') return;
      if (event.data.type === 'video-session-ended' || event.data.type === 'video-session-closed') {
        setActiveVideoSession(null);
      }
    };
    window.addEventListener('message', handlePopupMessage);
    return () => window.removeEventListener('message', handlePopupMessage);
  }, []);

  // Derived form variables for both overview and forms sections
  const pendingForms = assignedForms.filter(form => !form.completed);
  const highPriorityForms = pendingForms.filter(form => form.priority === 'high');
  const otherForms = pendingForms.filter(form => form.priority !== 'high');
  const sortedPendingForms = [...highPriorityForms, ...otherForms];

  // canBookAppointment is now managed as state — updated in loadPatientData and real-time subscription

  // Diagnosis data is now in clientDiagnoses state (subscribed from Firestore above)

  // Check if a video appointment can be joined (15 min before to 30 min after)
  const canJoinVideoSession = (appointment: Appointment): { canJoin: boolean; reason?: string; minutesUntil?: number } => {
    if (appointment.modality !== 'video') return { canJoin: false, reason: 'Not a video appointment' };
    if (appointment.status === 'cancelled') return { canJoin: false, reason: 'Appointment not active' };
    if (appointment.status === 'completed') return { canJoin: false, reason: 'Session already completed' };

    const now = new Date();
    const apptDate = parseApptDateTime(new Date(appointment.appointmentDate), appointment.startTime);

    const fifteenBefore = new Date(apptDate.getTime() - 15 * 60 * 1000);
    const thirtyAfter = new Date(apptDate.getTime() + 30 * 60 * 1000);

    if (now < fifteenBefore) {
      const minutesUntil = Math.floor((fifteenBefore.getTime() - now.getTime()) / (60 * 1000));
      return { canJoin: false, reason: 'Too early to join', minutesUntil };
    }
    if (now > thirtyAfter) return { canJoin: false, reason: 'Appointment window has passed' };
    return { canJoin: true };
  };

  // Handlers
  const handleLogout = () => {
    navigate('/');
  };

  const handleEndVideoSession = async () => {
    const appointment = activeVideoSession;
    setActiveVideoSession(null);

    // Mark appointment as completed in Firestore
    if (appointment?.id) {
      try {
        await firebaseAppointmentService.completeAppointment(appointment.id);
      } catch (err) {
        console.error('Failed to mark appointment as completed:', err);
      }
    }
  };

  // Client data from Firestore patient record
  const clientData = {
    reasonForVisit: '',
    symptoms: '',
    zipCode: currentPatient?.zipCode || '',
    primaryConcerns: [] as string[],
    currentDoctors: [] as { name: string; specialty: string; phone: string; email: string; practiceName: string; address: string; }[],
    medications: [] as { name: string; dosage: string; frequency: string; prescribedBy: string; }[],
    allergies: [] as { type: 'medication' | 'food' | 'environmental'; name: string; reaction: string; severity: 'mild' | 'moderate' | 'severe'; }[],
    chronicConditions: [] as string[],
    emergencyContacts: currentPatient?.emergencyContact ? [{
      name: currentPatient.emergencyContact.name,
      phone: currentPatient.emergencyContact.phone,
      relationship: currentPatient.emergencyContact.relationship,
      isAuthorizedForMedicalDecisions: false,
    }] : [],
    caregiver: undefined,
    insurance: {
      provider: currentPatient?.insurance?.provider || '',
      planType: '',
      memberId: currentPatient?.insurance?.policyNumber || '',
      groupNumber: currentPatient?.insurance?.groupNumber || '',
      copay: '',
      deductible: '',
      outOfPocketMax: ''
    },
    preferredPharmacy: currentPharmacy,
    formsAndConsents: {
      medicalRelease: false,
      hipaaConsent: false,
      caregiverAuthorization: false
    }
  };

  const handleProfileUpdate = async (data: any) => {
    if (!resolvedPatientId) return;

    try {
      // Build update object from intake form data
      const updates: Partial<Patient> = {};

      // Map intake form fields to Patient fields
      if (data.emergencyContacts && data.emergencyContacts.length > 0) {
        const ec = data.emergencyContacts[0];
        updates.emergencyContact = {
          name: ec.name || '',
          relationship: ec.relationship || '',
          phone: ec.phone || '',
          email: ec.email || '',
        };
      }

      if (data.insurance) {
        updates.insurance = {
          provider: data.insurance.provider || '',
          policyNumber: data.insurance.memberId || '',
          groupNumber: data.insurance.groupNumber || '',
          subscriberName: data.insurance.subscriberName || '',
        };
      }

      if (data.zipCode) updates.zipCode = data.zipCode;
      if (data.allergies) updates.allergies = Array.isArray(data.allergies) ? data.allergies.map((a: any) => `${a.name} (${a.severity})`).join(', ') : data.allergies;
      if (data.medications) updates.currentMedications = Array.isArray(data.medications) ? data.medications.map((m: any) => `${m.name} ${m.dosage}`).join(', ') : data.medications;
      if (data.chronicConditions) updates.medicalHistory = Array.isArray(data.chronicConditions) ? data.chronicConditions.join(', ') : data.chronicConditions;
      if (data.reasonForVisit) updates.presentingConcern = data.reasonForVisit;

      await FirebasePatientService.updatePatient(resolvedPatientId, updates);

      // Refresh local patient data
      const updatedPatient = await FirebasePatientService.getPatient(resolvedPatientId);
      setCurrentPatient(updatedPatient || null);

      if (data.preferredPharmacy) {
        setCurrentPharmacy(data.preferredPharmacy);
      }

      toast.success('Profile Updated', 'Your profile has been saved successfully.');
      logClientActivity('form_submitted', 'Profile updated', `${user?.displayName || 'Client'} updated their profile information.`, { formType: 'profile_update' });
      setActiveSection('overview');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Update Failed', 'Could not save your profile. Please try again.');
    }
  };

  const handlePharmacyUpdate = (pharmacy: Pharmacy) => {
    setCurrentPharmacy(pharmacy);
    toast.success('Pharmacy Updated', `Your preferred pharmacy is now ${pharmacy.name}.`);
    setActiveSection('overview');
    console.log('Updated pharmacy:', pharmacy);
  };

  // Filter pharmacies based on client's zip code
  const getPharmaciesForClient = (): Pharmacy[] => {
    if (!clientData.zipCode) return availablePharmacies;

    return availablePharmacies.filter(pharmacy =>
      pharmacy.serviceZipCodes.includes(clientData.zipCode!)
    ).map(pharmacy => ({
      ...pharmacy,
      distanceFromClient: pharmacy.zipCode === clientData.zipCode ? 0.5 : 2.5 // Mock distance
    })).sort((a, b) => (a.distanceFromClient || 999) - (b.distanceFromClient || 999));
  };

  return (
    <div className="app">
      <Header
        showSessionStatus={false}
        onOpenProfileSettings={() => setActiveSection('updateProfile')}
      />
      
      <main id="main-content" className="page">
        <div className="container">
          {/* Welcome Header */}
          <div style={{ marginBottom: '2rem' }}>
            <h1 className="page-title">
              {timeGreeting}, {clientFirstName}!
            </h1>
            <p className="page-subtitle">
              Manage your therapy appointments and access your wellness resources
            </p>
          </div>

          {/* Client Profile Summary Bar */}
          {currentPatient && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: '8px',
              padding: '0.75rem 1.25rem',
              marginBottom: '1rem',
              color: 'white'
            }}>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, fontSize: '1rem' }}>
                  {currentPatient.firstName} {currentPatient.lastName}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                  DOB: {currentPatient.dateOfBirth ? currentPatient.dateOfBirth.split('-').length === 3 ? `${currentPatient.dateOfBirth.split('-')[1]}/${currentPatient.dateOfBirth.split('-')[2]}/${currentPatient.dateOfBirth.split('-')[0]}` : currentPatient.dateOfBirth : 'N/A'}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                  Insurance: {formatInsurance(currentPatient.insurance?.provider)}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                  Provider: {provider?.name || 'N/A'}
                </span>
              </div>
              <span style={{ padding: '0.2rem 0.6rem', background: '#10B981', color: 'white', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 500 }}>Active</span>
            </div>
          )}

          {/* Navigation Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '0.75rem',
            marginBottom: '2rem'
          }}>

            <button onClick={() => setActiveSection('overview')} style={{ background: activeSection === 'overview' ? '#D4A017' : '#6b7280', border: 'none', borderRadius: '0.5rem', padding: '1rem', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '600' }}>Overview</h3>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem' }}>Back to dashboard</p>
            </button>
            <button
              onClick={canBookAppointment ? () => navigate('/book-appointment') : undefined}
              disabled={!canBookAppointment}
              style={{
                background: canBookAppointment ? '#10b981' : '#6b7280',
                border: 'none',
                borderRadius: '0.5rem',
                padding: '1rem',
                cursor: canBookAppointment ? 'pointer' : 'not-allowed',
                textAlign: 'left',
                transition: 'all 0.2s',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.25rem',
                opacity: canBookAppointment ? 1 : 0.6
              }}
            >
              <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '600' }}>
                {canBookAppointment ? 'Book Appointment' : '📋 Complete Forms First'}
              </h3>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: '0.95rem' }}>
                {canBookAppointment ? 'Schedule your visit' : 'Required forms pending'}
              </p>
            </button>
            <button onClick={() => setActiveSection('messages')} style={{ background: activeSection === 'messages' ? '#D4A017' : '#3b82f6', border: 'none', borderRadius: '0.5rem', padding: '1rem', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: '0.25rem', position: 'relative' }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                Messages
                {unreadCount > 0 && (
                  <span style={{
                    background: '#ef4444',
                    color: 'white',
                    borderRadius: '50%',
                    minWidth: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: '700'
                  }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </h3>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: '0.95rem' }}>Chat with provider</p>
            </button>
            <button onClick={() => setActiveSection('clientInfo')} style={{ background: activeSection === 'clientInfo' ? '#D4A017' : '#6b7280', border: 'none', borderRadius: '0.5rem', padding: '1rem', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '600' }}>Treatment Plan</h3>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem' }}>Goals, diagnosis & more</p>
            </button>
            <button onClick={() => setActiveSection('providerInfo')} style={{ background: activeSection === 'providerInfo' ? '#D4A017' : '#6b7280', border: 'none', borderRadius: '0.5rem', padding: '1rem', cursor: 'pointer', textAlign: 'center', transition: 'all 0.3s', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '1.2rem', fontWeight: '600' }}>Provider Info</h3>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem' }}>View provider profile</p>
            </button>
            <button onClick={() => setActiveSection('resources')} style={{ background: activeSection === 'resources' ? '#D4A017' : '#6b7280', border: 'none', borderRadius: '0.5rem', padding: '1rem', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '600' }}>Resources</h3>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem' }}>Helpful materials</p>
            </button>
            <button onClick={() => setActiveSection('viewHistory')} style={{ background: activeSection === 'viewHistory' ? '#D4A017' : '#6b7280', border: 'none', borderRadius: '0.5rem', padding: '1rem', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '600' }}>View History</h3>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem' }}>Past appointments</p>
            </button>
            <button onClick={() => setActiveSection('updateProfile')} style={{ background: activeSection === 'updateProfile' ? '#D4A017' : '#6b7280', border: 'none', borderRadius: '0.5rem', padding: '1rem', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '600' }}>Update Profile</h3>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem' }}>Manage information</p>
            </button>
            {/* Always show Forms to Complete button - Sarah will see "no forms" message if none assigned */}
            <button onClick={() => setActiveSection('formsToComplete')} style={{ background: activeSection === 'formsToComplete' ? '#D4A017' : '#6b7280', border: 'none', borderRadius: '0.5rem', padding: '1rem', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '600' }}>Forms to Complete</h3>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: '0.95rem' }}>Required forms</p>
            </button>
            <button onClick={() => setActiveSection('completedForms')} style={{ background: activeSection === 'completedForms' ? '#D4A017' : '#6b7280', border: 'none', borderRadius: '0.5rem', padding: '1rem', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '600' }}>Completed Forms</h3>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: '0.95rem' }}>View submitted forms</p>
            </button>
            <button onClick={() => setActiveSection('getHelp')} style={{ background: activeSection === 'getHelp' ? '#D4A017' : '#6b7280', border: 'none', borderRadius: '0.5rem', padding: '1rem', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '1.1rem', fontWeight: '600' }}>Get Help</h3>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem' }}>Contact support</p>
            </button>
          </div>


          {/* Bottom Section: Dynamic Content */}
          <div style={{ marginBottom: '2rem' }}>
            {(() => {
              switch (activeSection) {
                case 'overview':
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'start' }}>
                      {/* RIGHT COLUMN: Quick Actions (order: 2) */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', order: 2 }}>
                        <h2 style={{ fontSize: '1.3rem', margin: 0, color: 'white', fontWeight: '700' }}>Quick Actions</h2>

                        {/* Pending Forms */}
                        {sortedPendingForms.length > 0 ? (
                          <div style={{
                            background: sortedPendingForms.some(f => f.priority === 'high') ? '#fecaca' : '#fce79a',
                            borderRadius: '0.75rem',
                            padding: '1.25rem',
                            color: 'black'
                          }}>
                            <h3 style={{ fontSize: '1.1rem', marginTop: 0, marginBottom: '0.5rem', fontWeight: '600' }}>
                              Forms to Complete ({sortedPendingForms.length})
                            </h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              {sortedPendingForms.slice(0, 3).map((assignment) => {
                                const template = formTemplates.find(t => t.id === assignment.formTemplateId)
                                  || { id: assignment.formTemplateId, name: assignment.formTemplateName || assignment.formTemplateId, category: assignment.category || 'assessment', required: false, description: '' };

                                return (
                                  <div key={assignment.id} style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    background: assignment.priority === 'high' ? '#dc2626' : assignment.priority === 'medium' ? '#d97706' : '#6b7280',
                                    borderRadius: '0.5rem',
                                    padding: '0.6rem 0.75rem',
                                    color: 'white'
                                  }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontWeight: '600', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {template.name}
                                      </div>
                                      {template.required && (
                                        <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>Required for booking</div>
                                      )}
                                    </div>
                                    <button
                                      onClick={() => setActiveFormAssignment(assignment)}
                                      style={{
                                        background: 'rgba(255,255,255,0.2)',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        borderRadius: '0.25rem',
                                        padding: '0.4rem 0.6rem',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        fontWeight: '500',
                                        flexShrink: 0
                                      }}
                                    >
                                      Complete
                                    </button>
                                  </div>
                                );
                              })}
                              {sortedPendingForms.length > 3 && (
                                <button
                                  onClick={() => setActiveSection('formsToComplete')}
                                  style={{
                                    background: '#D4A017',
                                    border: 'none',
                                    borderRadius: '0.5rem',
                                    padding: '0.5rem 0.75rem',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    fontWeight: '600'
                                  }}
                                >
                                  View All {sortedPendingForms.length} Forms
                                </button>
                              )}
                            </div>
                          </div>
                        ) : assignedForms.length > 0 ? (
                          <div style={{
                            background: '#d1fae5',
                            borderRadius: '0.75rem',
                            padding: '1rem',
                            color: '#065f46'
                          }}>
                            <h3 style={{ fontSize: '1rem', margin: '0 0 0.25rem 0', fontWeight: '600' }}>All Forms Complete</h3>
                            <p style={{ fontSize: '0.9rem', margin: 0 }}>Great job! You can now book appointments.</p>
                          </div>
                        ) : null}

                        {/* Pending Homework */}
                        {(() => {
                          const pendingHomework = clientHomework.filter(hw => hw.status !== 'completed');
                          if (pendingHomework.length === 0) return null;
                          return (
                            <div style={{
                              background: '#e0e7ff',
                              borderRadius: '0.75rem',
                              padding: '1.25rem',
                              color: '#1e1b4b'
                            }}>
                              <h3 style={{ fontSize: '1.1rem', marginTop: 0, marginBottom: '0.5rem', fontWeight: '600' }}>
                                Homework ({pendingHomework.length})
                              </h3>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {pendingHomework.slice(0, 3).map((hw) => (
                                  <div key={hw.id} style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    background: '#6366f1',
                                    borderRadius: '0.5rem',
                                    padding: '0.6rem 0.75rem',
                                    color: 'white'
                                  }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontWeight: '600', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {hw.title}
                                      </div>
                                      {hw.dueDate && (
                                        <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                                          Due: {new Date(hw.dueDate).toLocaleDateString()}
                                        </div>
                                      )}
                                    </div>
                                    <button
                                      onClick={() => { setActiveSection('clientInfo'); setActiveTreatmentSubSection('homework'); }}
                                      style={{
                                        background: 'rgba(255,255,255,0.2)',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        borderRadius: '0.25rem',
                                        padding: '0.4rem 0.6rem',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        fontWeight: '500',
                                        flexShrink: 0
                                      }}
                                    >
                                      View
                                    </button>
                                  </div>
                                ))}
                                {pendingHomework.length > 3 && (
                                  <button
                                    onClick={() => { setActiveSection('clientInfo'); setActiveTreatmentSubSection('homework'); }}
                                    style={{
                                      background: '#4f46e5',
                                      border: 'none',
                                      borderRadius: '0.5rem',
                                      padding: '0.5rem 0.75rem',
                                      color: 'white',
                                      cursor: 'pointer',
                                      fontSize: '0.875rem',
                                      fontWeight: '600'
                                    }}
                                  >
                                    View All Homework
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        {/* No actions needed */}
                        {sortedPendingForms.length === 0 && assignedForms.length === 0 && clientHomework.filter(hw => hw.status !== 'completed').length === 0 && (
                          <div style={{
                            background: '#d1fae5',
                            borderRadius: '0.75rem',
                            padding: '1rem',
                            color: '#065f46'
                          }}>
                            <h3 style={{ fontSize: '1rem', margin: '0 0 0.25rem 0', fontWeight: '600' }}>You're all caught up!</h3>
                            <p style={{ fontSize: '0.9rem', margin: 0 }}>No pending forms or homework.</p>
                          </div>
                        )}
                      </div>

                      {/* LEFT COLUMN: Upcoming Appointments (order: 1) */}

                      {/* Upcoming Appointments Section */}
                      {(() => {
                        const now = new Date();

                        // Get upcoming appointments (scheduled/confirmed only)
                        const upcomingAppointments = clientAppointments
                          .filter(apt => {
                            const appointmentDate = parseApptDateTime(new Date(apt.appointmentDate), apt.startTime);

                            // Show scheduled/confirmed only — completed appointments no longer shown
                            if (apt.status !== 'scheduled' && apt.status !== 'confirmed') {
                              return false;
                            }

                            // Filter out past appointments (more than 30 minutes after start time)
                            const thirtyMinutesAfter = new Date(appointmentDate.getTime() + 30 * 60 * 1000);

                            return now < thirtyMinutesAfter;
                          })
                          .sort((a, b) => {
                            const dateA = toLocalDate(new Date(a.appointmentDate)).getTime();
                            const dateB = toLocalDate(new Date(b.appointmentDate)).getTime();
                            if (dateA !== dateB) return dateA - dateB;
                            return a.startTime.localeCompare(b.startTime);
                          });

                        const nextAppointment = upcomingAppointments[0];

                        // Check if appointment is within 2 days for reminder
                        const twoDaysFromNow = new Date();
                        twoDaysFromNow.setDate(twoDaysFromNow.getDate() + 2);
                        twoDaysFromNow.setHours(23, 59, 59, 999);

                        const showReminder = nextAppointment && toLocalDate(new Date(nextAppointment.appointmentDate)) <= twoDaysFromNow;

                        return (
                          <div style={{
                            background: '#6CA6C1',
                            borderRadius: '0.75rem',
                            padding: '1.5rem',
                            marginBottom: '1.5rem'
                          }}>
                            <h2 style={{ fontSize: '1.5rem', marginTop: 0, marginBottom: '1rem', color: 'black' }}>
                              Upcoming Appointments
                            </h2>
                            {nextAppointment ? (
                              <div>
                                {showReminder && (
                                  <div style={{
                                    background: '#F59E0B',
                                    color: 'black',
                                    padding: '0.75rem 1rem',
                                    borderRadius: '8px',
                                    marginBottom: '1rem',
                                    fontSize: '0.875rem',
                                    fontWeight: '600'
                                  }}>
                                    Reminder: You have an appointment coming up soon!
                                  </div>
                                )}
                                <div style={{
                                  background: 'white',
                                  borderRadius: '8px',
                                  padding: '1.25rem'
                                }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                                    <div>
                                      <p style={{ color: '#d1d5db', margin: '0 0 0.25rem 0', fontSize: '0.75rem', fontWeight: '500' }}>Date</p>
                                      <p style={{ color: 'black', margin: 0, fontWeight: '600' }}>
                                        {toLocalDate(new Date(nextAppointment.appointmentDate)).toLocaleDateString('en-US', {
                                          weekday: 'short',
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric'
                                        })}
                                      </p>
                                    </div>
                                    <div>
                                      <p style={{ color: '#d1d5db', margin: '0 0 0.25rem 0', fontSize: '0.75rem', fontWeight: '500' }}>Time</p>
                                      <p style={{ color: 'black', margin: 0, fontWeight: '600' }}>
                                        {formatTime12Hour(nextAppointment.startTime)}
                                      </p>
                                    </div>
                                    <div>
                                      <p style={{ color: '#d1d5db', margin: '0 0 0.25rem 0', fontSize: '0.75rem', fontWeight: '500' }}>Type</p>
                                      <p style={{ color: 'black', margin: 0, fontWeight: '600' }}>
                                        {nextAppointment.modality === 'video' ? 'Video Session' : 'In-Person'}
                                      </p>
                                    </div>
                                    <div>
                                      <p style={{ color: '#d1d5db', margin: '0 0 0.25rem 0', fontSize: '0.75rem', fontWeight: '500' }}>Provider</p>
                                      <p style={{ color: 'black', margin: 0, fontWeight: '600' }}>{provider?.name || 'Your Provider'}</p>
                                    </div>
                                  </div>

                                  {/* Join Session Button */}
                                  {(() => {
                                    const canJoin = canJoinVideoSession(nextAppointment);

                                    if (canJoin.canJoin && nextAppointment.modality === 'video') {
                                      return (
                                        <button
                                          onClick={() => {
                                            // Show location confirmation before launching video
                                            setPendingVideoAppointment(nextAppointment);
                                            setShowLocationModal(true);
                                          }}
                                          style={{
                                            padding: '16px 24px',
                                            backgroundColor: '#10b981',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            fontSize: '16px',
                                            width: '100%',
                                            textAlign: 'center'
                                          }}
                                        >
                                          Join Video Session Now
                                        </button>
                                      );
                                    } else if (!canJoin.canJoin && canJoin.minutesUntil !== undefined) {
                                      return (
                                        <div style={{
                                          padding: '12px',
                                          backgroundColor: '#fef3c7',
                                          border: '1px solid #fbbf24',
                                          borderRadius: '6px',
                                          color: 'black',
                                          fontSize: '14px',
                                          textAlign: 'center',
                                          fontWeight: '500'
                                        }}>
                                          Session starts in {canJoin.minutesUntil + 15} minutes. Join button will appear 15 minutes before.
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}

                                  {/* Action Buttons */}
                                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                                    <button
                                      onClick={() => setAppointmentToReschedule(nextAppointment)}
                                      style={{
                                        background: '#059669',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '0.5rem 1rem',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        fontWeight: '500'
                                      }}
                                    >
                                      Reschedule
                                    </button>
                                    <button
                                      onClick={() => setAppointmentToCancel(nextAppointment)}
                                      style={{
                                        background: '#6b7280',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '0.5rem 1rem',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        fontWeight: '500'
                                      }}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={() => setEmergencyCancellationAppointment(nextAppointment)}
                                      style={{
                                        background: '#dc2626',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '0.5rem 1rem',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        fontWeight: '500'
                                      }}
                                    >
                                      ⚠️ Emergency Cancel
                                    </button>
                                  </div>
                                </div>

                                {/* Show/Hide All Appointments Toggle */}
                                {upcomingAppointments.length > 1 && (
                                  <button
                                    onClick={() => setShowAllAppointments(!showAllAppointments)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#059669',
                                      cursor: 'pointer',
                                      fontSize: '0.875rem',
                                      fontWeight: '500',
                                      padding: '0.5rem 0',
                                      marginTop: '0.75rem',
                                      textDecoration: 'underline',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.25rem'
                                    }}
                                  >
                                    {showAllAppointments ? '▼' : '▶'} {showAllAppointments ? 'Hide' : 'View all'} {upcomingAppointments.length} upcoming {upcomingAppointments.length === 1 ? 'appointment' : 'appointments'}
                                  </button>
                                )}

                                {/* Expanded Appointments List */}
                                {showAllAppointments && upcomingAppointments.length > 1 && (
                                  <div style={{ marginTop: '1rem' }}>
                                    {upcomingAppointments.slice(1).map((appointment) => (
                                      <div
                                        key={appointment.id}
                                        style={{
                                          background: 'white',
                                          borderRadius: '8px',
                                          padding: '1rem',
                                          marginBottom: '0.75rem',
                                          border: '1px solid #e5e7eb'
                                        }}
                                      >
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                          <div>
                                            <p style={{ color: '#d1d5db', margin: '0 0 0.25rem 0', fontSize: '0.75rem', fontWeight: '500' }}>Date</p>
                                            <p style={{ color: 'black', margin: 0, fontWeight: '600', fontSize: '0.875rem' }}>
                                              {toLocalDate(new Date(appointment.appointmentDate)).toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric'
                                              })}
                                            </p>
                                          </div>
                                          <div>
                                            <p style={{ color: '#d1d5db', margin: '0 0 0.25rem 0', fontSize: '0.75rem', fontWeight: '500' }}>Time</p>
                                            <p style={{ color: 'black', margin: 0, fontWeight: '600', fontSize: '0.875rem' }}>
                                              {formatTime12Hour(appointment.startTime)}
                                            </p>
                                          </div>
                                          <div>
                                            <p style={{ color: '#d1d5db', margin: '0 0 0.25rem 0', fontSize: '0.75rem', fontWeight: '500' }}>Type</p>
                                            <p style={{ color: 'black', margin: 0, fontWeight: '600', fontSize: '0.875rem', textTransform: 'capitalize' }}>
                                              {appointment.modality === 'video' ? 'Video Call' : 'In-Person'}
                                            </p>
                                          </div>
                                          <div>
                                            <p style={{ color: '#d1d5db', margin: '0 0 0.25rem 0', fontSize: '0.75rem', fontWeight: '500' }}>Provider</p>
                                            <p style={{ color: 'black', margin: 0, fontWeight: '600', fontSize: '0.875rem' }}>
                                              {appointment.providerName || provider?.name || 'Dr. Emily Carter'}
                                            </p>
                                          </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                          {/* Join Video Session - only shows within the join window */}
                                          {(() => {
                                            const joinCheck = canJoinVideoSession(appointment);
                                            if (joinCheck.canJoin) {
                                              return (
                                                <button
                                                  onClick={() => {
                                                    setPendingVideoAppointment(appointment);
                                                    setShowLocationModal(true);
                                                  }}
                                                  style={{
                                                    background: '#10b981',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '0.5rem 1rem',
                                                    color: 'white',
                                                    cursor: 'pointer',
                                                    fontSize: '0.875rem',
                                                    fontWeight: '700'
                                                  }}
                                                >
                                                  Join Video Session
                                                </button>
                                              );
                                            }
                                            return null;
                                          })()}
                                          <button
                                            onClick={() => setAppointmentToReschedule(appointment)}
                                            style={{
                                              background: '#059669',
                                              border: 'none',
                                              borderRadius: '4px',
                                              padding: '0.5rem 1rem',
                                              color: 'white',
                                              cursor: 'pointer',
                                              fontSize: '0.875rem',
                                              fontWeight: '500'
                                            }}
                                          >
                                            Reschedule
                                          </button>
                                          <button
                                            onClick={() => setAppointmentToCancel(appointment)}
                                            style={{
                                              background: '#6b7280',
                                              border: 'none',
                                              borderRadius: '4px',
                                              padding: '0.5rem 1rem',
                                              color: 'white',
                                              cursor: 'pointer',
                                              fontSize: '0.875rem',
                                              fontWeight: '500'
                                            }}
                                          >
                                            Cancel
                                          </button>
                                          <button
                                            onClick={() => setEmergencyCancellationAppointment(appointment)}
                                            style={{
                                              background: '#dc2626',
                                              border: 'none',
                                              borderRadius: '4px',
                                              padding: '0.5rem 1rem',
                                              color: 'white',
                                              cursor: 'pointer',
                                              fontSize: '0.875rem',
                                              fontWeight: '500'
                                            }}
                                          >
                                            ⚠️ Emergency Cancel
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p style={{ color: 'black', margin: 0, fontSize: '1.1rem' }}>No upcoming appointments</p>
                            )}
                          </div>
                        );
                      })()}

                      {/* Outstanding Copays Section - TODO: Re-enable when billing feature is implemented */}
                      {/* <div style={{
                        background: '#fce79a',
                        borderRadius: '0.75rem',
                        padding: '1.5rem',
                        color: 'black'
                      }}>
                        <h2 style={{ fontSize: '1.5rem', marginTop: 0, marginBottom: '0.5rem' }}>
                          Outstanding Copays
                        </h2>
                        <p style={{ fontSize: '1.3rem', margin: '0 0 1rem 0' }}>
                          You have 1 upcoming appointment with unpaid copays
                        </p>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => toast.info('Coming Soon', 'Online payments coming soon. Please contact your provider for payment options.')}
                            style={{
                              background: '#6b7280',
                              border: 'none',
                              borderRadius: '0.5rem',
                              padding: '0.75rem 1rem',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '1rem'
                            }}
                          >
                            Pay $40.00 - {provider?.name || 'Provider'}
                          </button>
                          <button
                            onClick={canBookAppointment ? () => navigate('/book-appointment') : undefined}
                            disabled={!canBookAppointment}
                            style={{
                              background: canBookAppointment ? '#D4A017' : '#6b7280',
                              border: 'none',
                              borderRadius: '0.5rem',
                              padding: '0.75rem 1rem',
                              color: 'white',
                              cursor: canBookAppointment ? 'pointer' : 'not-allowed',
                              fontSize: '1rem',
                              fontWeight: '600',
                              opacity: canBookAppointment ? 1 : 0.6
                            }}
                          >
                            {canBookAppointment ? 'Book Appointment' : 'Complete Forms First'}
                          </button>
                        </div>
                      </div> */}
                    </div>
                  );
                case 'formsToComplete':
                  return (
                    <div style={{
                      background: '#fce79a',
                      borderRadius: '0.75rem',
                      padding: '1.5rem',
                      color: 'black',
                      marginBottom: '1rem'
                    }}>
                      <h2 style={{ fontSize: '1.5rem', marginTop: 0, marginBottom: '0.5rem' }}>
                        Forms to Complete
                      </h2>
                      {assignedForms.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                          <p style={{ fontSize: '1.3rem', margin: '0 0 1rem 0', color: '#d1d5db' }}>
                            No Forms Assigned
                          </p>
                          <p style={{ fontSize: '1rem', margin: 0, color: '#d1d5db' }}>
                            No forms have been assigned to you at this time.
                          </p>
                        </div>
                      ) : pendingForms.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                          <p style={{ fontSize: '1.3rem', margin: '0 0 1rem 0', color: '#059669' }}>
                            All Forms Complete!
                          </p>
                          <p style={{ fontSize: '1rem', margin: 0, color: '#374151' }}>
                            You have completed all assigned forms. You're eligible to book appointments.
                          </p>
                        </div>
                      ) : (
                        <>
                          <p style={{ fontSize: '1.3rem', margin: '0 0 1rem 0' }}>
                            {sortedPendingForms.some(f => f.priority === 'high')
                              ? `You have ${sortedPendingForms.length} forms that require your immediate attention (including high priority forms)`
                              : `You have ${sortedPendingForms.length} forms that require your attention`
                            }
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {sortedPendingForms.map((assignment) => {
                              const template = formTemplates.find(t => t.id === assignment.formTemplateId)
                                || { id: assignment.formTemplateId, name: assignment.formTemplateName || assignment.formTemplateId, category: assignment.category || 'assessment', required: false, description: '' };

                              const priorityColors = {
                                high: { bg: '#fee2e2', border: '#dc2626', text: '#991b1b' },
                                medium: { bg: '#fef3c7', border: '#d97706', text: '#92400e' },
                                low: { bg: '#ddd6fe', border: '#7c3aed', text: '#5b21b6' }
                              };

                              const colors = priorityColors[assignment.priority];

                              return (
                                <div key={assignment.id} style={{
                                  background: colors.bg,
                                  border: `2px solid ${colors.border}`,
                                  borderRadius: '0.5rem',
                                  padding: '1rem'
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                                    <div style={{ flex: 1 }}>
                                      <h3 style={{ margin: '0 0 0.25rem 0', color: colors.text, fontSize: '1.1rem', fontWeight: '600' }}>
                                        {template.name}
                                      </h3>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                        <span style={{
                                          background: colors.border,
                                          color: 'white',
                                          padding: '0.125rem 0.5rem',
                                          borderRadius: '0.25rem',
                                          fontSize: '0.75rem',
                                          fontWeight: '600',
                                          textTransform: 'uppercase'
                                        }}>
                                          {assignment.priority} Priority
                                        </span>
                                        <span style={{ color: colors.text, fontSize: '0.875rem', textTransform: 'capitalize' }}>
                                          {template.category.replace('-', ' ')}
                                        </span>
                                      </div>
                                      {template.description && (
                                        <p style={{ margin: '0 0 0.5rem 0', color: colors.text, fontSize: '0.875rem' }}>
                                          {template.description}
                                        </p>
                                      )}
                                      {assignment.dueDate && (
                                        <p style={{ margin: 0, color: colors.text, fontSize: '0.875rem' }}>
                                          Due: {new Date(assignment.dueDate).toLocaleDateString()}
                                        </p>
                                      )}
                                    </div>
                                    <button
                                      onClick={() => {
                                        // Open form viewer
                                        setActiveFormAssignment(assignment);
                                      }}
                                      style={{
                                        background: colors.border,
                                        border: 'none',
                                        borderRadius: '0.5rem',
                                        padding: '0.5rem 1rem',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        fontWeight: '600'
                                      }}
                                    >
                                      Fill Out Form
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {pendingForms.length > 0 && (
                            <div style={{
                              marginTop: '1.5rem',
                              padding: '1rem',
                              background: '#e0f2fe',
                              border: '2px solid #0284c7',
                              borderRadius: '0.5rem',
                              textAlign: 'center'
                            }}>
                              <p style={{ margin: '0 0 0.5rem 0', color: '#0c4a6e', fontWeight: '600' }}>
                                Appointment Booking Status
                              </p>
                              <p style={{ margin: 0, color: '#0c4a6e', fontSize: '0.875rem' }}>
                                {canBookAppointment
                                  ? 'You can book appointments once all required forms are completed'
                                  : 'Complete required forms to unlock appointment booking'
                                }
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                case 'updateProfile':
                  return (
                    <div style={{ background: 'rgba(255, 255, 255, 0.15)', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: '2rem', width: '100%', maxWidth: '100%', margin: 0 }}>
                      <CollapsibleSection title="Update Profile" defaultExpanded={true}>
                        <div style={{ width: '100%', margin: '2rem 0', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: '2rem' }}>
                          <ClientIntakeForm isInitialSetup={false} existingData={clientData} clientInfo={currentPatient ? { firstName: currentPatient.firstName, lastName: currentPatient.lastName, dateOfBirth: currentPatient.dateOfBirth, phone: currentPatient.phone, email: currentPatient.email, address: currentPatient.address, city: currentPatient.city, state: currentPatient.state, zipCode: currentPatient.zipCode } : undefined} onSubmit={handleProfileUpdate} />

                          {/* Update Pharmacy Section at the bottom */}
                          <div style={{ marginTop: '2rem', borderTop: '2px solid rgba(255, 255, 255, 0.2)', paddingTop: '2rem' }}>
                            <CollapsibleSection title="Update Pharmacy" defaultExpanded={false}>
                              <div style={{ padding: '1rem', color: 'white' }}>
                                {/* Current Pharmacy Display */}
                                {currentPharmacy && (
                                  <div style={{
                                    background: '#E6F3FF',
                                    border: '2px solid #3B82F6',
                                    borderRadius: '0.5rem',
                                    padding: '1rem',
                                    marginBottom: '1.5rem'
                                  }}>
                                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#1E40AF' }}>
                                      🏪 Current Pharmacy
                                    </h4>
                                    <div style={{ color: '#1E40AF' }}>
                                      <p style={{ margin: '0.25rem 0', fontWeight: 'bold' }}>{currentPharmacy.name}</p>
                                      <p style={{ margin: '0.25rem 0', fontSize: '0.875rem' }}>{currentPharmacy.address}</p>
                                      <p style={{ margin: '0.25rem 0', fontSize: '0.875rem' }}>{currentPharmacy.phone}</p>
                                    </div>
                                  </div>
                                )}

                                <div style={{
                                  background: '#E6F3FF',
                                  border: '2px solid #3B82F6',
                                  borderRadius: '0.5rem',
                                  padding: '1rem',
                                  marginBottom: '1rem'
                                }}>
                                  <p style={{ margin: 0, color: '#1E40AF', fontSize: '0.875rem' }}>
                                    📍 Showing pharmacies serving zip code {clientData.zipCode} (sorted by distance)
                                  </p>
                                </div>

                                <h4 style={{ color: '#d1d5db', marginBottom: '1rem' }}>Select New Preferred Pharmacy:</h4>

                                <div style={{ display: 'grid', gap: '0.75rem' }}>
                                  {getPharmaciesForClient().map((pharmacy) => (
                                    <div key={pharmacy.id} style={{
                                      padding: '1rem',
                                      background: currentPharmacy?.id === pharmacy.id
                                        ? 'rgba(212, 160, 23, 0.2)'
                                        : 'rgba(255, 255, 255, 0.05)',
                                      borderRadius: '0.5rem',
                                      border: currentPharmacy?.id === pharmacy.id
                                        ? '2px solid #D4A017'
                                        : '1px solid rgba(156, 163, 175, 0.2)',
                                      transition: 'all 0.2s'
                                    }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1 }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                            <h4 style={{ margin: 0, color: 'white', fontSize: '1rem' }}>
                                              {pharmacy.name}
                                            </h4>
                                            {pharmacy.distanceFromClient !== undefined && (
                                              <span style={{
                                                background: '#10B981',
                                                color: 'white',
                                                padding: '0.125rem 0.5rem',
                                                borderRadius: '0.25rem',
                                                fontSize: '0.75rem',
                                                fontWeight: '600'
                                              }}>
                                                {pharmacy.distanceFromClient} mi
                                              </span>
                                            )}
                                          </div>
                                          <p style={{ margin: '0 0 0.25rem 0', color: '#d1d5db', fontSize: '0.875rem' }}>
                                            📍 {pharmacy.address}
                                          </p>
                                          <p style={{ margin: '0 0 0.25rem 0', color: '#d1d5db', fontSize: '0.875rem' }}>
                                            📞 {pharmacy.phone}
                                          </p>
                                          <p style={{ margin: '0 0 0.5rem 0', color: '#d1d5db', fontSize: '0.875rem' }}>
                                            🕒 {pharmacy.hours}
                                          </p>
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                            {pharmacy.acceptsInsurance.map((insurance, idx) => (
                                              <span key={idx} style={{
                                                background: clientData.insurance?.provider &&
                                                  pharmacy.acceptsInsurance.includes(clientData.insurance.provider)
                                                  ? '#16A34A'
                                                  : '#6B7280',
                                                color: 'white',
                                                padding: '0.125rem 0.5rem',
                                                borderRadius: '0.25rem',
                                                fontSize: '0.75rem'
                                              }}>
                                                {insurance}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                        <button
                                          onClick={() => handlePharmacyUpdate(pharmacy)}
                                          style={{
                                            background: currentPharmacy?.id === pharmacy.id ? '#6B7280' : '#D4A017',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '0.5rem',
                                            padding: '0.5rem 1rem',
                                            cursor: currentPharmacy?.id === pharmacy.id ? 'not-allowed' : 'pointer',
                                            marginLeft: '1rem',
                                            opacity: currentPharmacy?.id === pharmacy.id ? 0.6 : 1
                                          }}
                                          disabled={currentPharmacy?.id === pharmacy.id}
                                        >
                                          {currentPharmacy?.id === pharmacy.id ? 'Current' : 'Select'}
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </CollapsibleSection>
                          </div>

                          {/* Change Password Section */}
                          <div style={{ marginTop: '2rem', borderTop: '2px solid rgba(255, 255, 255, 0.2)', paddingTop: '2rem' }}>
                            <CollapsibleSection title="Change Password" defaultExpanded={false}>
                              <div style={{ padding: '1rem', color: 'white', maxWidth: '400px' }}>
                                <p style={{ color: '#d1d5db', fontSize: '0.875rem', marginTop: 0, marginBottom: '1rem' }}>
                                  Update your password to keep your account secure.
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                  <div>
                                    <label style={{ display: 'block', marginBottom: '0.25rem', color: '#d1d5db', fontSize: '0.875rem' }}>Current Password</label>
                                    <input
                                      type="password"
                                      id="client-current-password"
                                      placeholder="Enter current password"
                                      style={{
                                        width: '100%', padding: '0.75rem', borderRadius: '0.375rem',
                                        border: '1px solid rgba(156, 163, 175, 0.2)',
                                        background: 'rgba(255, 255, 255, 0.1)', color: 'white',
                                        fontSize: '0.875rem', boxSizing: 'border-box'
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <label style={{ display: 'block', marginBottom: '0.25rem', color: '#d1d5db', fontSize: '0.875rem' }}>New Password</label>
                                    <input
                                      type="password"
                                      id="client-new-password"
                                      placeholder="At least 8 characters"
                                      style={{
                                        width: '100%', padding: '0.75rem', borderRadius: '0.375rem',
                                        border: '1px solid rgba(156, 163, 175, 0.2)',
                                        background: 'rgba(255, 255, 255, 0.1)', color: 'white',
                                        fontSize: '0.875rem', boxSizing: 'border-box'
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <label style={{ display: 'block', marginBottom: '0.25rem', color: '#d1d5db', fontSize: '0.875rem' }}>Confirm New Password</label>
                                    <input
                                      type="password"
                                      id="client-confirm-password"
                                      placeholder="Confirm new password"
                                      style={{
                                        width: '100%', padding: '0.75rem', borderRadius: '0.375rem',
                                        border: '1px solid rgba(156, 163, 175, 0.2)',
                                        background: 'rgba(255, 255, 255, 0.1)', color: 'white',
                                        fontSize: '0.875rem', boxSizing: 'border-box'
                                      }}
                                    />
                                  </div>
                                  <button
                                    onClick={async () => {
                                      const currentPw = (document.getElementById('client-current-password') as HTMLInputElement)?.value;
                                      const newPw = (document.getElementById('client-new-password') as HTMLInputElement)?.value;
                                      const confirmPw = (document.getElementById('client-confirm-password') as HTMLInputElement)?.value;
                                      if (!currentPw || !newPw || !confirmPw) {
                                        toast.warning('Missing Fields', 'Please fill in all password fields.');
                                        return;
                                      }
                                      if (newPw !== confirmPw) {
                                        toast.error('Mismatch', 'New passwords do not match.');
                                        return;
                                      }
                                      if (newPw.length < 8) {
                                        toast.error('Too Short', 'New password must be at least 8 characters.');
                                        return;
                                      }
                                      try {
                                        await firebaseAuthService.changePassword(currentPw, newPw);
                                        toast.success('Password Changed', 'Your password has been updated successfully.');
                                        (document.getElementById('client-current-password') as HTMLInputElement).value = '';
                                        (document.getElementById('client-new-password') as HTMLInputElement).value = '';
                                        (document.getElementById('client-confirm-password') as HTMLInputElement).value = '';
                                      } catch (error: any) {
                                        toast.error('Change Failed', error.message || 'Failed to change password.');
                                      }
                                    }}
                                    style={{
                                      background: '#D4A017', color: 'white', border: 'none',
                                      borderRadius: '0.375rem', padding: '0.75rem 1.5rem',
                                      fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
                                      alignSelf: 'flex-start', transition: 'all 0.2s'
                                    }}
                                  >
                                    Update Password
                                  </button>
                                </div>
                              </div>
                            </CollapsibleSection>
                          </div>

                          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                            <button onClick={() => setActiveSection('overview')} style={{ background: 'rgba(255, 255, 255, 0.2)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, padding: '0.5rem 2rem', cursor: 'pointer', marginTop: '1rem' }}>Back to Overview</button>
                          </div>
                        </div>
                      </CollapsibleSection>
                    </div>
                  );
                case 'providerInfo':
                  return (
                    <div style={{ background: 'rgba(255, 255, 255, 0.15)', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', padding: '2rem', width: '100%', maxWidth: '100%', margin: 0 }}>
                      <CollapsibleSection title="Provider Info" defaultExpanded={true}>
                        <div style={{ width: '100%', margin: '2rem 0', padding: '2rem', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', color: 'white' }}>
                          <h1 style={{ marginBottom: '2rem', fontSize: '2rem', fontWeight: 700, color: 'white' }}>Provider Information</h1>
                          {!provider ? (
                            <div style={{ textAlign: 'center', padding: '2rem', color: 'rgba(255,255,255,0.7)' }}>
                              <p>No provider information available.</p>
                            </div>
                          ) : (
                          <>
                          <div style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem', display: 'flex', gap: '2rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            {/* Provider Photo & Upload */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                              <div style={{ minWidth: 120, minHeight: 120, width: 120, height: 120, background: 'rgba(255, 255, 255, 0.2)', borderRadius: '50%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48, color: 'rgba(255,255,255,0.7)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                {provider.photoUrl ? (
                                  <img src={provider.photoUrl} alt={provider.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <span>👩‍⚕️</span>
                                )}
                              </div>
                              {/* Play Button */}
                              <button
                                onClick={() => setShowVideoModal(true)}
                                style={{
                                  background: '#2E8555',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '16px',
                                  fontSize: '0.95rem',
                                  fontWeight: '600',
                                  padding: '0.5rem 1rem',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  transition: 'all 0.2s ease',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.background = '#235e46';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.background = '#2E8555';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                }}
                              >
                                <span>Play Video</span>
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                  style={{ marginLeft: '2px' }}
                                >
                                  <path d="M8 5v14l11-7z"/>
                                </svg>
                              </button>
                            </div>
                            {/* Provider Details */}
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: 8 }}>
                                <h2 style={{ fontSize: '1.5rem', margin: 0, color: 'white', fontWeight: 700 }}>{provider.name}</h2>
                                <span style={{ background: '#2E8555', color: 'white', borderRadius: '16px', fontSize: '0.95rem', fontWeight: 600, padding: '0.25rem 1rem' }}>Active</span>
                              </div>
                              {provider.bio && <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem', marginBottom: 16, lineHeight: 1.5 }}>{provider.bio}</div>}
                              <div style={{ marginBottom: 8, color: 'rgba(255,255,255,0.9)' }}><strong>Location:</strong> {provider.location || 'Not provided'}</div>
                              <div style={{ marginBottom: 8, color: 'rgba(255,255,255,0.9)' }}><strong>Phone:</strong> {provider.phone || 'Not provided'}</div>
                              <div style={{ marginBottom: 8, color: 'rgba(255,255,255,0.9)' }}><strong>Languages:</strong> {provider.languages?.join(', ') || 'English'}</div>
                              <div style={{ marginBottom: 8, color: 'rgba(255,255,255,0.9)' }}><strong>Insurance Accepted:</strong> {provider.insuranceAccepted?.join(', ') || 'Blue Cross Blue Shield, Aetna, Cigna, UnitedHealthcare'}</div>
                              {/* Rating hidden — will enable when review system is built */}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                            {/* Education - Left Side (50%) */}
                            <div style={{ flex: 1, minWidth: '250px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '1.5rem' }}>
                              <h3 style={{ marginBottom: '1rem', color: 'white', fontWeight: 600 }}>Education</h3>
                              <ul style={{ listStyle: 'none', padding: 0, color: 'rgba(255,255,255,0.9)', fontSize: '1rem' }}>
                                {provider.education?.length ? provider.education.map((edu, index) => (
                                  <li key={index} style={{ marginBottom: '0.5rem' }}>• {edu}</li>
                                )) : (
                                  <li style={{ marginBottom: '0.5rem' }}>• Licensed Clinical Social Worker</li>
                                )}
                              </ul>
                            </div>
                            {/* Certifications - Right Side (50%) */}
                            <div style={{ flex: 1, minWidth: '250px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '1.5rem' }}>
                              <h3 style={{ marginBottom: '1rem', color: 'white', fontWeight: 600 }}>Certifications</h3>
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gridTemplateRows: 'repeat(3, auto)',
                                gap: '0.5rem',
                                color: 'rgba(255,255,255,0.9)',
                                fontSize: '1rem',
                                gridAutoFlow: 'column'
                              }}>
                                {provider.certifications?.length ? provider.certifications.map((cert, index) => (
                                  <div key={index} style={{ marginBottom: '0.5rem' }}>• {cert}</div>
                                )) : (
                                  <div style={{ marginBottom: '0.5rem' }}>• Licensed Clinical Social Worker (LCSW)</div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' }}>
                            <h3 style={{ marginBottom: '1rem', color: 'white', fontWeight: 600 }}>Specialties</h3>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                              {provider.specialties?.length ? provider.specialties.map((specialty, index) => (
                                <span key={index} style={{ padding: '0.5rem 1rem', background: '#2E8555', color: 'white', borderRadius: '16px', fontSize: '0.95rem', fontWeight: 500 }}>{specialty}</span>
                              )) : (
                                <span style={{ padding: '0.5rem 1rem', background: '#2E8555', color: 'white', borderRadius: '16px', fontSize: '0.95rem', fontWeight: 500 }}>General Therapy</span>
                              )}
                            </div>
                          </div>
                          <div style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' }}>
                            <h3 style={{ marginBottom: '1rem', color: 'white', fontWeight: 600 }}>Availability</h3>
                            <div style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px', color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>{provider.availability || 'Monday - Friday, 9:00 AM - 5:00 PM'}</div>
                          </div>
                          <div style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '1.5rem' }}>
                            <h3 style={{ marginBottom: '1rem', color: 'white', fontWeight: 600 }}>Languages</h3>
                            <div style={{ display: 'flex', gap: '1rem', color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>
                              {provider.languages?.length ? provider.languages.map((lang, index) => (
                                <span key={index}>• {lang}</span>
                              )) : (
                                <span>• English</span>
                              )}
                            </div>
                          </div>
                          </>
                          )}
                        </div>
                      </CollapsibleSection>
                    </div>
                  );
                case 'resources':
                  // Map diagnosis categories to tailored resources
                  const diagnosisResourceMap: Record<string, { title: string; color: string; bg: string; border: string; resources: { name: string; url: string; desc: string }[] }> = {
                    'Anxiety': {
                      title: 'Anxiety & Panic Resources',
                      color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe',
                      resources: [
                        { name: 'ADAA', url: 'https://adaa.org', desc: 'Anxiety & Depression Association — treatment info, support groups' },
                        { name: 'Anxiety Canada', url: 'https://www.anxietycanada.com/resources/mindshift-cbt/', desc: 'MindShift CBT app — free anxiety management' },
                        { name: 'CCI Anxiety Modules', url: 'https://www.cci.health.wa.gov.au/Resources/Looking-After-Yourself/Anxiety', desc: 'Free self-help workbooks for anxiety' },
                        { name: 'Dare App', url: 'https://www.dfrnt.com/dare-app/', desc: 'Evidence-based app for panic & anxiety relief' },
                      ]
                    },
                    'Depression': {
                      title: 'Depression & Mood Resources',
                      color: '#1e40af', bg: '#eff6ff', border: '#bfdbfe',
                      resources: [
                        { name: 'DBSA', url: 'https://www.dbsalliance.org', desc: 'Depression & Bipolar Support Alliance — peer support, wellness tools' },
                        { name: 'CCI Depression Modules', url: 'https://www.cci.health.wa.gov.au/Resources/Looking-After-Yourself/Depression', desc: 'Free self-help workbooks for depression' },
                        { name: 'Behavioral Activation Guide', url: 'https://www.therapistaid.com/therapy-worksheet/behavioral-activation', desc: 'Activity scheduling to improve mood' },
                        { name: 'NIMH Depression Info', url: 'https://www.nimh.nih.gov/health/topics/depression', desc: 'Research-based information on depression' },
                      ]
                    },
                    'Bipolar': {
                      title: 'Bipolar Disorder Resources',
                      color: '#9333ea', bg: '#faf5ff', border: '#e9d5ff',
                      resources: [
                        { name: 'DBSA', url: 'https://www.dbsalliance.org', desc: 'Depression & Bipolar Support Alliance — peer support & education' },
                        { name: 'NAMI Bipolar', url: 'https://www.nami.org/About-Mental-Illness/Mental-Health-Conditions/Bipolar-Disorder', desc: 'Understanding bipolar disorder' },
                        { name: 'IBPF', url: 'https://ibpf.org', desc: 'International Bipolar Foundation — resources & stories' },
                        { name: 'Mood Tracking Apps', url: 'https://www.healthline.com/health/bipolar-disorder/top-iphone-android-apps', desc: 'Track mood patterns to share with your provider' },
                      ]
                    },
                    'Trauma': {
                      title: 'Trauma & PTSD Resources',
                      color: '#b45309', bg: '#fffbeb', border: '#fde68a',
                      resources: [
                        { name: 'NCPTSD', url: 'https://www.ptsd.va.gov', desc: 'National Center for PTSD — education, self-help tools' },
                        { name: 'PTSD Coach App', url: 'https://www.ptsd.va.gov/appvid/mobile/ptsdcoach_app.asp', desc: 'Free app with coping tools for trauma symptoms' },
                        { name: 'RAINN', url: 'https://www.rainn.org', desc: 'Rape, Abuse & Incest National Network — 24/7 support' },
                        { name: 'Grounding Techniques', url: 'https://www.therapistaid.com/therapy-worksheet/grounding-techniques', desc: 'Coping skills for flashbacks & dissociation' },
                      ]
                    },
                    'OCD': {
                      title: 'OCD Resources',
                      color: '#0e7490', bg: '#ecfeff', border: '#a5f3fc',
                      resources: [
                        { name: 'IOCDF', url: 'https://iocdf.org', desc: 'International OCD Foundation — treatment, support groups' },
                        { name: 'NOCD', url: 'https://www.treatmyocd.com', desc: 'ERP therapy resources and provider directory' },
                        { name: 'OCD Workbook', url: 'https://www.therapistaid.com/therapy-worksheet/ocd-cycle', desc: 'Understanding the OCD cycle' },
                      ]
                    },
                    'ADHD': {
                      title: 'ADHD Resources',
                      color: '#ea580c', bg: '#fff7ed', border: '#fed7aa',
                      resources: [
                        { name: 'CHADD', url: 'https://chadd.org', desc: 'Children & Adults with ADHD — education, advocacy' },
                        { name: 'ADDitude Magazine', url: 'https://www.additudemag.com', desc: 'Strategies, treatment info, expert advice' },
                        { name: 'How to ADHD', url: 'https://www.youtube.com/@HowtoADHD', desc: 'YouTube channel with practical ADHD tips' },
                        { name: 'Focusmate', url: 'https://www.focusmate.com', desc: 'Virtual co-working for accountability' },
                      ]
                    },
                    'Eating': {
                      title: 'Eating Disorder Resources',
                      color: '#be185d', bg: '#fdf2f8', border: '#fbcfe8',
                      resources: [
                        { name: 'NEDA', url: 'https://www.nationaleatingdisorders.org', desc: 'National Eating Disorders Association — helpline, screening' },
                        { name: 'ANAD', url: 'https://anad.org', desc: 'National Association of Anorexia & Associated Disorders — free support' },
                        { name: 'Recovery Record App', url: 'https://www.recoveryrecord.com', desc: 'Meal & symptom tracking app for recovery' },
                      ]
                    },
                    'Substance Use': {
                      title: 'Substance Use & Recovery Resources',
                      color: '#dc2626', bg: '#fef2f2', border: '#fecaca',
                      resources: [
                        { name: 'SAMHSA Helpline', url: 'https://www.samhsa.gov/find-help/national-helpline', desc: 'Free 24/7 treatment referral — 1-800-662-4357' },
                        { name: 'AA Meeting Finder', url: 'https://www.aa.org/find-aa', desc: 'Find local & online AA meetings' },
                        { name: 'NA Meeting Finder', url: 'https://www.na.org/meetingsearch/', desc: 'Find Narcotics Anonymous meetings' },
                        { name: 'SMART Recovery', url: 'https://www.smartrecovery.org', desc: 'Science-based addiction recovery support' },
                      ]
                    },
                    'Personality': {
                      title: 'Personality Disorder Resources',
                      color: '#6d28d9', bg: '#f5f3ff', border: '#ddd6fe',
                      resources: [
                        { name: 'NEABPD', url: 'https://www.borderlinepersonalitydisorder.org', desc: 'National Education Alliance for BPD — family resources' },
                        { name: 'DBT Self-Help', url: 'https://www.dbtselfhelp.com', desc: 'Dialectical Behavior Therapy skills & worksheets' },
                        { name: 'Emotion Regulation Guide', url: 'https://www.therapistaid.com/therapy-worksheet/dbt-emotion-regulation', desc: 'DBT-based emotional regulation skills' },
                      ]
                    },
                    'Sleep': {
                      title: 'Sleep & Insomnia Resources',
                      color: '#4338ca', bg: '#eef2ff', border: '#c7d2fe',
                      resources: [
                        { name: 'Sleep Foundation', url: 'https://www.sleepfoundation.org', desc: 'Evidence-based sleep hygiene tips' },
                        { name: 'CBT-i Coach App', url: 'https://mobile.va.gov/app/cbt-i-coach', desc: 'Free app for insomnia based on CBT' },
                        { name: 'CCI Sleep Modules', url: 'https://www.cci.health.wa.gov.au/Resources/Looking-After-Yourself/Sleep', desc: 'Free self-help workbooks for better sleep' },
                      ]
                    },
                    'Relational': {
                      title: 'Relationship & Family Resources',
                      color: '#0d9488', bg: '#f0fdfa', border: '#99f6e4',
                      resources: [
                        { name: 'Gottman Institute', url: 'https://www.gottman.com/blog/', desc: 'Research-based relationship advice & tools' },
                        { name: 'AAMFT Therapist Finder', url: 'https://www.aamft.org/Therapist_Locator/TherapistLocator.aspx', desc: 'Find a marriage & family therapist' },
                        { name: 'Couples Communication', url: 'https://www.therapistaid.com/therapy-worksheet/fair-fighting-rules', desc: 'Fair fighting rules for healthier conflict' },
                      ]
                    },
                    'Dissociative': {
                      title: 'Dissociative Disorder Resources',
                      color: '#475569', bg: '#f8fafc', border: '#cbd5e1',
                      resources: [
                        { name: 'ISSTD', url: 'https://www.isst-d.org', desc: 'International Society for Traumatic Stress Studies' },
                        { name: 'Grounding Exercises', url: 'https://www.therapistaid.com/therapy-worksheet/grounding-techniques', desc: 'Techniques for managing dissociation' },
                      ]
                    },
                    'Developmental': {
                      title: 'Autism & Neurodevelopmental Resources',
                      color: '#0369a1', bg: '#f0f9ff', border: '#bae6fd',
                      resources: [
                        { name: 'Autism Society', url: 'https://autismsociety.org', desc: 'Resources, advocacy, and support for autism' },
                        { name: 'ASAN', url: 'https://autisticadvocacy.org', desc: 'Autistic Self Advocacy Network — by and for autistic people' },
                      ]
                    },
                  };

                  // Get unique categories from client's active diagnoses
                  const activeDiagnosisCategories = Array.from(new Set(
                    clientDiagnoses
                      .filter(d => d.status === 'active')
                      .map(d => {
                        // Match diagnosis code to category from ICD-10 data
                        const code = d.code || '';
                        if (code.startsWith('F40') || code.startsWith('F41')) return 'Anxiety';
                        if (code.startsWith('F32') || code.startsWith('F33') || code.startsWith('F34')) return 'Depression';
                        if (code.startsWith('F31')) return 'Bipolar';
                        if (code.startsWith('F43')) return 'Trauma';
                        if (code.startsWith('F42') || code === 'F45.22' || code === 'F63.3' || code === 'L98.1') return 'OCD';
                        if (code.startsWith('F60')) return 'Personality';
                        if (code.startsWith('F90')) return 'ADHD';
                        if (code.startsWith('F84')) return 'Developmental';
                        if (code.startsWith('F50')) return 'Eating';
                        if (code.startsWith('F10') || code.startsWith('F11') || code.startsWith('F12') || code.startsWith('F13') || code.startsWith('F15') || code.startsWith('F19')) return 'Substance Use';
                        if (code.startsWith('F51')) return 'Sleep';
                        if (code.startsWith('F44') || code === 'F48.1') return 'Dissociative';
                        if (code.startsWith('Z')) return 'Relational';
                        return null;
                      })
                      .filter(Boolean) as string[]
                  ));

                  return (
                    <div style={{ background: 'rgba(255, 255, 255, 0.15)', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '2rem', width: '100%', maxWidth: '100%', margin: 0 }}>
                      <CollapsibleSection title="Wellness Resources" defaultExpanded={true}>
                        <div style={{ padding: '2rem', color: 'white' }}>
                          <div style={{ display: 'grid', gap: '1.5rem' }}>

                            {/* 911/988 Disclaimer — always pinned at very top */}
                            <div style={{ background: '#7f1d1d', borderRadius: '8px', padding: '1rem 1.5rem', border: '2px solid #dc2626', textAlign: 'center' }}>
                              <p style={{ margin: 0, color: '#fecaca', fontSize: '1rem', fontWeight: '700' }}>
                                If you are in active crisis, please dial <a href="tel:911" style={{ color: '#fff', textDecoration: 'underline' }}>911</a> or <a href="tel:988" style={{ color: '#fff', textDecoration: 'underline' }}>988</a>.
                              </p>
                            </div>

                            {/* Crisis Resources — always pinned at top */}
                            <div style={{ background: '#fef2f2', borderRadius: '8px', padding: '1.5rem', border: '1px solid #fecaca' }}>
                              <h3 style={{ color: '#991b1b', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                Crisis Resources
                              </h3>
                              <ul style={{ color: '#475569', lineHeight: '2', listStyle: 'none', padding: 0, margin: 0 }}>
                                <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span>National Suicide Prevention Lifeline:</span>
                                  <a href="tel:988" style={{ color: '#dc2626', fontWeight: 'bold', textDecoration: 'none' }}>988</a>
                                  <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>(call or text)</span>
                                </li>
                                <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span>Crisis Text Line: Text <strong>HOME</strong> to</span>
                                  <a href="sms:741741?body=HOME" style={{ color: '#dc2626', fontWeight: 'bold', textDecoration: 'none' }}>741741</a>
                                </li>
                                <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span>SAMHSA National Helpline:</span>
                                  <a href="tel:1-800-662-4357" style={{ color: '#dc2626', fontWeight: 'bold', textDecoration: 'none' }}>1-800-662-4357</a>
                                  <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>(free, 24/7)</span>
                                </li>
                                <li style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span>National Domestic Violence Hotline:</span>
                                  <a href="tel:1-800-799-7233" style={{ color: '#dc2626', fontWeight: 'bold', textDecoration: 'none' }}>1-800-799-7233</a>
                                </li>
                              </ul>
                            </div>

                            {/* Diagnosis-Specific Resources (alphabetized) */}
                            {activeDiagnosisCategories.length > 0 && (
                              <>
                                <div style={{ background: 'rgba(212, 160, 23, 0.15)', borderRadius: '8px', padding: '1rem 1.5rem', border: '1px solid rgba(212, 160, 23, 0.3)' }}>
                                  <p style={{ margin: 0, color: '#fbbf24', fontSize: '0.95rem' }}>
                                    Based on your care plan, we've highlighted resources tailored to your needs.
                                  </p>
                                </div>
                                {[...activeDiagnosisCategories].sort().map(category => {
                                  const resourceGroup = diagnosisResourceMap[category];
                                  if (!resourceGroup) return null;
                                  return (
                                    <div key={category} style={{ background: resourceGroup.bg, borderRadius: '8px', padding: '1.5rem', border: `1px solid ${resourceGroup.border}` }}>
                                      <h3 style={{ color: resourceGroup.color, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        {resourceGroup.title}
                                      </h3>
                                      <ul style={{ color: '#475569', lineHeight: '2', listStyle: 'none', padding: 0, margin: 0 }}>
                                        {resourceGroup.resources.map(r => (
                                          <li key={r.url}><a href={r.url} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>{r.name}</a> — {r.desc}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  );
                                })}
                              </>
                            )}

                            {/* Mindfulness & Meditation — always shown */}
                            <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '1.5rem', border: '1px solid #bbf7d0' }}>
                              <h3 style={{ color: '#166534', marginBottom: '1rem' }}>Mindfulness & Meditation</h3>
                              <ul style={{ color: '#475569', lineHeight: '2', listStyle: 'none', padding: 0, margin: 0 }}>
                                <li><a href="https://www.headspace.com" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>Headspace</a> — Guided meditation & sleep</li>
                                <li><a href="https://www.calm.com" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>Calm</a> — Sleep stories, breathing & relaxation</li>
                                <li><a href="https://insighttimer.com" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>Insight Timer</a> — Free meditation sessions (45k+)</li>
                                <li><a href="https://www.youtube.com/results?search_query=guided+meditation+10+minutes" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>YouTube Guided Meditations</a> — Free, no app needed</li>
                              </ul>
                            </div>

                            {/* Mental Health Education — always shown */}
                            <div style={{ background: '#eff6ff', borderRadius: '8px', padding: '1.5rem', border: '1px solid #bfdbfe' }}>
                              <h3 style={{ color: '#1e40af', marginBottom: '1rem' }}>Mental Health Education</h3>
                              <ul style={{ color: '#475569', lineHeight: '2', listStyle: 'none', padding: 0, margin: 0 }}>
                                <li><a href="https://www.nami.org/Home" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>NAMI</a> — National Alliance on Mental Illness (support groups, education)</li>
                                <li><a href="https://www.psychologytoday.com" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>Psychology Today</a> — Articles on mental health topics</li>
                                <li><a href="https://www.nimh.nih.gov/health" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>NIMH</a> — National Institute of Mental Health (research-based info)</li>
                                <li><a href="https://adaa.org" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>ADAA</a> — Anxiety & Depression Association of America</li>
                              </ul>
                            </div>

                            {/* Self-Help Tools — always shown */}
                            <div style={{ background: '#fefce8', borderRadius: '8px', padding: '1.5rem', border: '1px solid #fef08a' }}>
                              <h3 style={{ color: '#854d0e', marginBottom: '1rem' }}>Self-Help Tools</h3>
                              <ul style={{ color: '#475569', lineHeight: '2', listStyle: 'none', padding: 0, margin: 0 }}>
                                <li><a href="https://www.therapistaid.com/worksheets" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>Therapist Aid Worksheets</a> — CBT, DBT, and coping worksheets</li>
                                <li><a href="https://positivepsychology.com/cbt-cognitive-behavioral-therapy-techniques-worksheets/" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>Positive Psychology CBT Tools</a> — Evidence-based exercises</li>
                                <li><a href="https://www.cci.health.wa.gov.au/Resources/Looking-After-Yourself" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>CCI Self-Help Resources</a> — Free modules (anxiety, depression, self-esteem)</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </CollapsibleSection>
                    </div>
                  );
                case 'clientInfo':
                  return (
                    <div style={{ background: 'rgba(255, 255, 255, 0.15)', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '2rem', width: '100%', maxWidth: '100%', margin: 0 }}>
                      {/* Client Profile Summary — compact header */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        background: 'rgba(255, 255, 255, 0.08)',
                        borderRadius: '8px',
                        padding: '1rem 1.5rem',
                        marginBottom: '1.5rem',
                        color: 'white'
                      }}>
                        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600, fontSize: '1.1rem' }}>
                            {currentPatient ? `${currentPatient.firstName} ${currentPatient.lastName}` : user?.displayName || 'N/A'}
                          </span>
                          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                            DOB: {currentPatient?.dateOfBirth ? currentPatient.dateOfBirth.split('-').length === 3 ? `${currentPatient.dateOfBirth.split('-')[1]}/${currentPatient.dateOfBirth.split('-')[2]}/${currentPatient.dateOfBirth.split('-')[0]}` : currentPatient.dateOfBirth : 'N/A'}
                          </span>
                          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                            Insurance: {formatInsurance(currentPatient?.insurance?.provider)}
                          </span>
                          <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                            Provider: {provider?.name || 'N/A'}
                          </span>
                        </div>
                        <span style={{ padding: '0.25rem 0.75rem', background: '#10B981', color: 'white', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 500 }}>Active</span>
                      </div>

                      {/* Treatment Plan Sub-navigation — direct, no intermediate toggle */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: '0.75rem',
                        marginBottom: '2rem'
                      }}>
                        {[
                          { id: 'overview', label: 'Treatment Overview' },
                          { id: 'diagnosisInfo', label: 'Diagnosis Info' },
                          { id: 'myPrescriptions', label: 'My Prescriptions' },
                          { id: 'homework', label: 'Homework' },
                        ].map(tab => (
                          <button
                            key={tab.id}
                            onClick={() => setActiveTreatmentSubSection(tab.id as TreatmentSubSection)}
                            style={{
                              background: activeTreatmentSubSection === tab.id ? '#D4A017' : '#6b7280',
                              border: 'none',
                              borderRadius: '0.5rem',
                              padding: '0.75rem',
                              cursor: 'pointer',
                              textAlign: 'center',
                              transition: 'all 0.2s',
                              color: 'white',
                              fontSize: '0.875rem',
                              fontWeight: '600'
                            }}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>

                      {/* Treatment Sub-content */}
                      {(() => {
                        switch (activeTreatmentSubSection) {
                                        case 'overview':
                                          return (
                                            <div style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '2rem', color: 'white' }}>
                                              <h2 style={{ color: '#58A6FF', marginBottom: '1.5rem' }}>Treatment Plan</h2>
                                              {clientTreatmentPlans.length === 0 ? (
                                                <div style={{ textAlign: 'center', padding: '2rem' }}>
                                                  <p style={{ color: '#d1d5db', fontSize: '1rem' }}>Your treatment plan will be updated by your provider after your sessions.</p>
                                                </div>
                                              ) : (
                                                clientTreatmentPlans.filter(p => p.status === 'active' || p.status === 'draft').map(plan => (
                                                  <div key={plan.id} style={{ marginBottom: '1.5rem' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                                      <div>
                                                        <h3 style={{ margin: '0 0 4px', fontSize: '1.1rem' }}>{plan.diagnosis}</h3>
                                                        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', margin: 0 }}>
                                                          By {plan.providerName} &middot; Updated {plan.updatedAt.toLocaleDateString()}
                                                          {plan.reviewDate && ` · Next review: ${plan.reviewDate}`}
                                                        </p>
                                                      </div>
                                                      <span style={{
                                                        padding: '4px 12px',
                                                        borderRadius: '12px',
                                                        fontSize: '0.8rem',
                                                        fontWeight: 600,
                                                        background: plan.status === 'active' ? '#D4A017' : '#6b7280',
                                                        color: '#1f2937',
                                                        textTransform: 'capitalize'
                                                      }}>{plan.status}</span>
                                                    </div>
                                                    {plan.presentingProblem && (
                                                      <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', marginBottom: '1rem', lineHeight: '1.6' }}>
                                                        {plan.presentingProblem}
                                                      </p>
                                                    )}
                                                    {plan.sessionFrequency && (
                                                      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', marginBottom: '1rem' }}>
                                                        Sessions: {plan.sessionFrequency}
                                                        {plan.estimatedDuration && ` · Estimated duration: ${plan.estimatedDuration}`}
                                                      </p>
                                                    )}
                                                    {plan.goals.map((goal, gi) => (
                                                      <div key={goal.id} style={{
                                                        background: 'rgba(255,255,255,0.05)',
                                                        borderRadius: '8px',
                                                        padding: '1rem',
                                                        marginBottom: '0.75rem',
                                                        borderLeft: `3px solid ${goal.status === 'achieved' ? '#10B981' : goal.status === 'active' ? '#D4A017' : '#EF4444'}`
                                                      }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                                                          <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>Goal {gi + 1}: {goal.description}</span>
                                                          <span style={{
                                                            fontSize: '0.75rem',
                                                            color: goal.status === 'achieved' ? '#10B981' : goal.status === 'active' ? '#D4A017' : '#EF4444',
                                                            textTransform: 'capitalize'
                                                          }}>{goal.status}</span>
                                                        </div>
                                                        {goal.objectives.map((obj, oi) => (
                                                          <div key={obj.id} style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                              <span style={{
                                                                width: '8px', height: '8px', borderRadius: '50%',
                                                                background: obj.status === 'achieved' ? '#10B981' : obj.status === 'in-progress' ? '#D4A017' : '#6b7280',
                                                                display: 'inline-block', flexShrink: 0
                                                              }} />
                                                              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>
                                                                {obj.description || `Objective ${oi + 1}`}
                                                              </span>
                                                              <span style={{
                                                                fontSize: '0.7rem',
                                                                color: 'rgba(255,255,255,0.4)',
                                                                textTransform: 'capitalize'
                                                              }}>({obj.status.replace('-', ' ')})</span>
                                                            </div>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    ))}
                                                  </div>
                                                ))
                                              )}
                                            </div>
                                          );
                                        case 'diagnosisInfo':
                                          return (
                                            <div style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '2rem', color: 'white' }}>
                                              <h2 style={{ color: '#58A6FF', marginBottom: '1.5rem' }}>Diagnosis Information</h2>
                                              {clientDiagnoses.length === 0 ? (
                                                <div style={{ textAlign: 'center', padding: '2rem' }}>
                                                  <p style={{ color: '#d1d5db', fontSize: '1rem' }}>No diagnoses recorded yet. Your provider will add diagnosis information here after your sessions.</p>
                                                </div>
                                              ) : (
                                                clientDiagnoses.map((diagnosis) => (
                                                  <div key={diagnosis.id} style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '1.5rem', marginBottom: '1rem' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                                                      <div>
                                                        <h3 style={{ marginBottom: '0.25rem', fontSize: '1.1rem' }}>{diagnosis.name}</h3>
                                                        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', margin: 0 }}>
                                                          ICD-10: {diagnosis.code}
                                                        </p>
                                                        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', margin: '4px 0 0' }}>
                                                          Diagnosed by {diagnosis.diagnosedBy} on {new Date(diagnosis.diagnosedDate).toLocaleDateString()}
                                                        </p>
                                                      </div>
                                                      <span style={{
                                                        padding: '0.25rem 0.75rem',
                                                        background: diagnosis.status === 'active' ? '#f59e0b' : diagnosis.status === 'resolved' ? '#10b981' : '#ef4444',
                                                        borderRadius: '16px',
                                                        fontSize: '0.8rem',
                                                        fontWeight: '500',
                                                        textTransform: 'capitalize'
                                                      }}>
                                                        {diagnosis.status}
                                                      </span>
                                                    </div>
                                                    {diagnosis.notes && (
                                                      <p style={{ color: 'rgba(255,255,255,0.7)', fontStyle: 'italic', margin: '0.5rem 0 0', fontSize: '0.9rem' }}>
                                                        "{diagnosis.notes}"
                                                      </p>
                                                    )}
                                                  </div>
                                                ))
                                              )}
                                              <p style={{ color: '#d1d5db', fontSize: '0.8rem', marginTop: '1rem', textAlign: 'center' }}>
                                                This information is managed by your healthcare provider.
                                              </p>
                                            </div>
                                          );
                                        case 'myPrescriptions':
                                          return (
                                            <div style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '2rem', color: 'white' }}>
                                              <h2 style={{ color: '#58A6FF', marginBottom: '1.5rem' }}>My Prescriptions</h2>
                                              {clientPrescriptions.length === 0 ? (
                                                <div style={{ textAlign: 'center', padding: '2rem' }}>
                                                  <p style={{ color: '#d1d5db', fontSize: '1rem' }}>No prescriptions recorded yet. Your provider will add prescriptions here when applicable.</p>
                                                </div>
                                              ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                                  {clientPrescriptions.map((rx) => (
                                                    <div key={rx.id} style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '1.25rem' }}>
                                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                                                        <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{rx.medicationName}</h3>
                                                        <span style={{
                                                          padding: '0.25rem 0.75rem',
                                                          background: rx.status === 'active' ? '#10b981' : rx.status === 'discontinued' ? '#ef4444' : '#6b7280',
                                                          borderRadius: '16px',
                                                          fontSize: '0.8rem',
                                                          fontWeight: '500',
                                                          textTransform: 'capitalize'
                                                        }}>
                                                          {rx.status}
                                                        </span>
                                                      </div>
                                                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem' }}>
                                                        <p style={{ margin: '2px 0' }}><strong style={{ color: '#e5e7eb' }}>Dosage:</strong> {rx.dosage}</p>
                                                        <p style={{ margin: '2px 0' }}><strong style={{ color: '#e5e7eb' }}>Frequency:</strong> {rx.frequency}</p>
                                                        <p style={{ margin: '2px 0' }}><strong style={{ color: '#e5e7eb' }}>Start:</strong> {new Date(rx.startDate).toLocaleDateString()}</p>
                                                        {rx.endDate && <p style={{ margin: '2px 0' }}><strong style={{ color: '#e5e7eb' }}>End:</strong> {new Date(rx.endDate).toLocaleDateString()}</p>}
                                                        {rx.refillsRemaining !== undefined && <p style={{ margin: '2px 0' }}><strong style={{ color: '#e5e7eb' }}>Refills:</strong> {rx.refillsRemaining}</p>}
                                                        {rx.pharmacy && <p style={{ margin: '2px 0' }}><strong style={{ color: '#e5e7eb' }}>Pharmacy:</strong> {rx.pharmacy}</p>}
                                                        <p style={{ margin: '2px 0' }}><strong style={{ color: '#e5e7eb' }}>Prescribed by:</strong> {rx.prescribedBy}</p>
                                                      </div>
                                                      {rx.notes && (
                                                        <p style={{ color: 'rgba(255,255,255,0.7)', fontStyle: 'italic', margin: '0.5rem 0 0', fontSize: '0.85rem' }}>
                                                          "{rx.notes}"
                                                        </p>
                                                      )}
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                              <p style={{ color: '#d1d5db', fontSize: '0.8rem', marginTop: '1rem', textAlign: 'center' }}>
                                                Prescriptions are managed by your healthcare provider. Contact your provider for changes.
                                              </p>
                                            </div>
                                          );
                                        case 'homework':
                                          return (
                                            <div style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px', padding: '2rem', color: 'white' }}>
                                              {clientHomework.length === 0 ? (
                                                <div style={{ textAlign: 'center', padding: '2rem' }}>
                                                  <p style={{ color: '#d1d5db', fontSize: '1rem' }}>No homework assignments yet. Your provider will add assignments here.</p>
                                                </div>
                                              ) : (() => {
                                                const pendingItems = clientHomework.filter(hw => hw.status !== 'completed');
                                                const completedItems = clientHomework.filter(hw => hw.status === 'completed');
                                                const statusColors: Record<string, { bg: string; border: string; badge: string }> = {
                                                  pending: { bg: '#EFF6FF', border: '#DBEAFE', badge: '#F59E0B' },
                                                  completed: { bg: '#F0FDF4', border: '#BBF7D0', badge: '#10B981' },
                                                  overdue: { bg: '#FEF2F2', border: '#FECACA', badge: '#EF4444' },
                                                };

                                                const renderCard = (hw: typeof clientHomework[0]) => {
                                                  const colors = statusColors[hw.status] || statusColors.pending;
                                                  return (
                                                    <div key={hw.id} style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '1.5rem' }}>
                                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                                                        <h3 style={{ color: '#1E293B', margin: 0 }}>{hw.title}</h3>
                                                        <span style={{ background: colors.badge, color: 'white', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.875rem', textTransform: 'capitalize' }}>{hw.status}</span>
                                                      </div>
                                                      {hw.description && <p style={{ color: '#475569', marginBottom: '0.75rem' }}>{hw.description}</p>}
                                                      <div style={{ display: 'flex', gap: '1rem', fontSize: '0.8rem', color: '#64748B', marginBottom: '0.75rem' }}>
                                                        {hw.dueDate && <span>Due: {new Date(hw.dueDate).toLocaleDateString()}</span>}
                                                        <span>By: {hw.assignedBy}</span>
                                                      </div>
                                                      {hw.status === 'pending' && (
                                                        completingHomeworkId === hw.id ? (
                                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                            <textarea
                                                              value={homeworkNotes}
                                                              onChange={(e) => setHomeworkNotes(e.target.value)}
                                                              placeholder="Add notes about your progress (optional)..."
                                                              rows={2}
                                                              style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '0.875rem', resize: 'vertical', color: '#1E293B' }}
                                                            />
                                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                              <button
                                                                onClick={async () => {
                                                                  try {
                                                                    await FirebaseHomeworkService.updateHomeworkStatus(hw.id, 'completed', user?.uid || '', 'client', homeworkNotes || undefined);
                                                                    setCompletingHomeworkId('');
                                                                    setHomeworkNotes('');
                                                                    toast.success('Completed', `"${hw.title}" marked as complete.`);
                                                                  } catch { toast.error('Error', 'Could not update homework.'); }
                                                                }}
                                                                style={{ background: '#10B981', color: 'white', padding: '0.5rem 1rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                                                              >
                                                                Submit
                                                              </button>
                                                              <button
                                                                onClick={() => { setCompletingHomeworkId(''); setHomeworkNotes(''); }}
                                                                style={{ background: '#6b7280', color: 'white', padding: '0.5rem 1rem', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                                                              >
                                                                Cancel
                                                              </button>
                                                            </div>
                                                          </div>
                                                        ) : (
                                                          <button
                                                            onClick={() => setCompletingHomeworkId(hw.id)}
                                                            style={{ background: '#10B981', color: 'white', padding: '0.5rem 1rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '600' }}
                                                          >
                                                            Mark Complete
                                                          </button>
                                                        )
                                                      )}
                                                      {hw.status === 'completed' && hw.clientNotes && (
                                                        <div style={{ background: 'rgba(16, 185, 129, 0.1)', padding: '0.5rem 0.75rem', borderRadius: '4px', marginTop: '0.5rem' }}>
                                                          <p style={{ color: '#475569', fontSize: '0.85rem', margin: 0 }}>Your notes: {hw.clientNotes}</p>
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                };

                                                return (
                                                  <div style={{ display: 'grid', gap: '1rem' }}>
                                                    {/* Active / Pending items first */}
                                                    {pendingItems.map(renderCard)}

                                                    {/* Completed section — collapsible */}
                                                    {completedItems.length > 0 && (
                                                      <div style={{ marginTop: '0.5rem' }}>
                                                        <button
                                                          onClick={() => setShowCompletedHomework(!showCompletedHomework)}
                                                          style={{
                                                            background: 'none', border: 'none', color: '#10B981',
                                                            fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer',
                                                            display: 'flex', alignItems: 'center', gap: '8px',
                                                            padding: '8px 0'
                                                          }}
                                                        >
                                                          <span style={{ fontSize: '12px' }}>{showCompletedHomework ? '▼' : '▶'}</span>
                                                          Completed ({completedItems.length})
                                                        </button>
                                                        {showCompletedHomework && (
                                                          <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.5rem', opacity: 0.85 }}>
                                                            {completedItems.map(renderCard)}
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })()}
                                            </div>
                                          );
                          default:
                            return null;
                        }
                      })()}
                    </div>
                  );
                case 'viewHistory':
                  return (
                    <div style={{ background: 'rgba(255, 255, 255, 0.15)', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '2rem', width: '100%', maxWidth: '100%', margin: 0 }}>
                      <CollapsibleSection title="Session History" defaultExpanded={true}>
                        <div style={{ padding: '2rem', color: 'white', textAlign: 'center' }}>
                          <p style={{ color: '#d1d5db', fontSize: '1rem' }}>No session history yet. Sessions will appear here after your appointments.</p>
                        </div>
                      </CollapsibleSection>
                    </div>
                  );
                case 'messages':
                  return (
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.15)',
                      borderRadius: '0.75rem',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      width: '100%',
                      maxWidth: '100%',
                      margin: 0,
                      height: 'calc(100vh - 400px)',
                      minHeight: '500px'
                    }}>
                      <ClientMessagesTab
                        onBack={() => setActiveSection('overview')}
                        providerId={provider?.id}
                        providerName={provider?.name}
                        clientId={resolvedPatientId}
                        clientName={currentPatient ? `${currentPatient.firstName} ${currentPatient.lastName}` : user?.displayName || 'Client'}
                      />
                    </div>
                  );
                case 'getHelp':
                  return (
                    <div style={{ background: 'rgba(255, 255, 255, 0.15)', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '2rem', width: '100%', maxWidth: '100%', margin: 0 }}>
                      <CollapsibleSection title="Get Support" defaultExpanded={true}>
                        <div style={{ padding: '2rem', color: 'white' }}>
                          <div style={{ display: 'grid', gap: '1.5rem' }}>
                            <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: '8px', padding: '1.5rem' }}>
                              <h3 style={{ color: '#DC2626', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                🚨 Crisis Support
                              </h3>
                              <p style={{ color: 'black', marginBottom: '1rem' }}>If you're experiencing a mental health emergency, please reach out immediately:</p>
                              <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem', color: 'black' }}>
                                <div><strong>Crisis Text Line:</strong> Text HOME to 741741</div>
                                <div><strong>National Suicide Prevention Lifeline:</strong> 988</div>
                                <div><strong>Emergency Services:</strong> 911</div>
                              </div>
                            </div>

                            <div style={{ background: '#EFF6FF', border: '1px solid #DBEAFE', borderRadius: '8px', padding: '1.5rem' }}>
                              <h3 style={{ color: '#d1d5db', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                📞 Contact Your Therapist
                              </h3>
                              <div style={{ display: 'grid', gap: '0.75rem', color: 'black' }}>
                                <div><strong>{provider?.name || 'Provider'}:</strong> {provider?.phone ? <a href={`tel:${provider.phone}`} style={{ color: '#1e40af' }}>{provider.phone}</a> : 'No phone listed'}</div>
                                {provider?.email && <div><strong>Email:</strong> <a href={`mailto:${provider.email}`} style={{ color: '#1e40af' }}>{provider.email}</a></div>}
                                <div><strong>After Hours:</strong> Leave voicemail for next business day response</div>
                              </div>
                              <button
                                onClick={() => setActiveSection('messages')}
                                style={{ marginTop: '1rem', background: '#3b82f6', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                              >
                                Send Message
                              </button>
                            </div>

                            <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '1.5rem' }}>
                              <h3 style={{ color: '#15803D', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                💬 {practiceInfo.practiceName || 'Practice Support'}
                              </h3>
                              <div style={{ display: 'grid', gap: '0.75rem', color: 'black' }}>
                                {practiceInfo.phone && (
                                  <div><strong>Scheduling:</strong> <a href={`tel:${practiceInfo.phone}`} style={{ color: '#15803D' }}>{practiceInfo.phone}</a></div>
                                )}
                                {practiceInfo.email && (
                                  <div><strong>Email:</strong> <a href={`mailto:${practiceInfo.email}`} style={{ color: '#15803D' }}>{practiceInfo.email}</a></div>
                                )}
                                {practiceInfo.website && (
                                  <div><strong>Website:</strong> <a href={practiceInfo.website.startsWith('http') ? practiceInfo.website : `https://${practiceInfo.website}`} target="_blank" rel="noopener noreferrer" style={{ color: '#15803D' }}>{practiceInfo.website}</a></div>
                                )}
                                {practiceInfo.officeAddress && (practiceInfo.officeAddress.street || practiceInfo.officeAddress.city) && (
                                  <div><strong>Address:</strong> {[practiceInfo.officeAddress.street, practiceInfo.officeAddress.city, practiceInfo.officeAddress.state, practiceInfo.officeAddress.zipCode].filter(Boolean).join(', ')}</div>
                                )}
                                {!practiceInfo.phone && !practiceInfo.email && (
                                  <div style={{ color: '#d1d5db', fontStyle: 'italic' }}>Contact information will appear here once your provider updates their practice settings.</div>
                                )}
                              </div>
                              {practiceInfo.phone && (
                                <button
                                  onClick={() => window.open(`tel:${practiceInfo.phone}`, '_self')}
                                  style={{ marginTop: '1rem', background: '#15803D', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
                                >
                                  Contact Office
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </CollapsibleSection>
                    </div>
                  );
                case 'completedForms':
                  return (
                    <div style={{ background: 'rgba(255, 255, 255, 0.15)', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '2rem', width: '100%', maxWidth: '100%', margin: 0 }}>
                      <CollapsibleSection title="Completed Forms" defaultExpanded={true}>
                        <div style={{ padding: '2rem', color: 'white' }}>
                          {loadingCompletedForms ? (
                            <div style={{ textAlign: 'center', padding: '3rem' }}>
                              <p style={{ fontSize: '1.1rem', color: '#d1d5db' }}>Loading completed forms...</p>
                            </div>
                          ) : completedFormResponses.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '3rem' }}>
                              <p style={{ fontSize: '1.2rem', color: '#d1d5db', marginBottom: '0.5rem' }}>
                                No Completed Forms
                              </p>
                              <p style={{ fontSize: '0.95rem', color: '#d1d5db' }}>
                                Forms you submit will appear here for download
                              </p>
                            </div>
                          ) : (
                            <div style={{ display: 'grid', gap: '1rem' }}>
                              {completedFormResponses.map((response) => (
                                <div key={response.id} style={{
                                  background: '#1f2937',
                                  border: '1px solid #374151',
                                  borderRadius: '8px',
                                  padding: '1.5rem'
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                      <h3 style={{ color: '#f9fafb', margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>
                                        {response.formTemplateName}
                                      </h3>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                        <span style={{
                                          background: '#10B981',
                                          color: 'white',
                                          padding: '0.125rem 0.5rem',
                                          borderRadius: '0.25rem',
                                          fontSize: '0.75rem',
                                          fontWeight: '600'
                                        }}>
                                          Completed
                                        </span>
                                        <span style={{ color: '#d1d5db', fontSize: '0.875rem' }}>
                                          {response.responses.length} responses
                                        </span>
                                      </div>
                                      <p style={{ color: '#d1d5db', fontSize: '0.875rem', margin: 0 }}>
                                        Submitted: {new Date(response.submittedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </CollapsibleSection>
                    </div>
                  );
                default:
                  return null;
              }
            })()}
          </div>
        </div>
      </main>

      {/* Video Modal Overlay */}
      {showVideoModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}>
          <div style={{
            position: 'relative',
            background: 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '80vh'
          }}>
            {/* Close Button */}
            <button
              onClick={() => setShowVideoModal(false)}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'rgba(0, 0, 0, 0.1)',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '18px',
                color: '#d1d5db'
              }}
            >
              ✕
            </button>

            {/* Video Content */}
            <div style={{ marginTop: '1rem' }}>
              <h3 style={{ margin: '0 0 1rem 0', color: '#1f2937' }}>
                Meet {provider?.name || 'Your Provider'}
              </h3>

              {provider?.videoUrl ? (
                <video
                  controls
                  autoPlay
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    backgroundColor: '#000'
                  }}
                >
                  <source src={provider.videoUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div style={{
                  background: '#f3f4f6',
                  borderRadius: '8px',
                  padding: '3rem',
                  textAlign: 'center',
                  color: '#d1d5db'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎥</div>
                  <p style={{ margin: 0, fontSize: '1.1rem' }}>
                    No video available for this provider yet.
                  </p>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                    The provider can upload an introduction video from their dashboard.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Location Confirmation Modal - Shows before video session */}
      {showLocationModal && pendingVideoAppointment && currentPatient && (
        <LocationConfirmationModal
          patientName={`${currentPatient.firstName} ${currentPatient.lastName}`}
          onConfirm={async (location) => {
            // Save location to appointment
            const updatedAppointment: Appointment = {
              ...pendingVideoAppointment,
              location: {
                latitude: location.latitude,
                longitude: location.longitude,
                timestamp: new Date().toISOString(),
                accuracy: location.accuracy,
              },
            } as Appointment;

            // Update appointment in service
            await firebaseAppointmentService.updateAppointment(updatedAppointment.id, {
              location: updatedAppointment.location
            });

            // Close location modal and launch video session in popup
            setShowLocationModal(false);
            setPendingVideoAppointment(null);

            const popupUrl = `/video-session?appointmentId=${encodeURIComponent(updatedAppointment.id)}&role=client`;
            const popup = window.open(
              popupUrl,
              `video-session-${updatedAppointment.id}`,
              'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no'
            );
            if (!popup) {
              // Popup blocked — fall back to inline overlay
              setActiveVideoSession(updatedAppointment);
            }
          }}
          onCancel={() => {
            // Close modal without launching video
            setShowLocationModal(false);
            setPendingVideoAppointment(null);
          }}
        />
      )}

      {/* Cancel Appointment Confirmation Modal */}
      {appointmentToCancel && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: '#16325B',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#f9fafb', fontSize: '1.5rem' }}>
              Cancel Appointment
            </h3>
            <p style={{ color: '#d1d5db', fontSize: '16px', marginBottom: '20px' }}>
              Are you sure you want to cancel this appointment?
            </p>

            {/* Appointment Details */}
            <div style={{
              background: '#1e3a5f',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ color: '#d1d5db', fontSize: '14px' }}>Date: </span>
                <span style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600' }}>
                  {toLocalDate(new Date(appointmentToCancel.appointmentDate)).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ color: '#d1d5db', fontSize: '14px' }}>Time: </span>
                <span style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600' }}>
                  {formatTime12Hour(appointmentToCancel.startTime)}
                </span>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ color: '#d1d5db', fontSize: '14px' }}>Type: </span>
                <span style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600' }}>
                  {appointmentToCancel.modality === 'video' ? 'Video Call' : 'In-Person'}
                </span>
              </div>
              <div>
                <span style={{ color: '#d1d5db', fontSize: '14px' }}>Provider: </span>
                <span style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600' }}>
                  {appointmentToCancel.providerName || provider?.name || 'Dr. Emily Carter'}
                </span>
              </div>
            </div>

            <p style={{ color: '#fbbf24', fontSize: '14px', marginBottom: '20px', fontWeight: '500' }}>
              ⚠️ Your provider will be notified of this cancellation.
            </p>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setAppointmentToCancel(null)}
                style={{
                  background: '#374151',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '12px 24px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Go Back
              </button>
              <button
                onClick={async () => {
                  // Cancel the appointment in Firestore
                  await firebaseAppointmentService.updateAppointment(appointmentToCancel.id, {
                    status: 'cancelled'
                  });
                  // Refresh appointments from Firestore (includes couples appointments)
                  if (resolvedPatientId) {
                    const appts = await firebaseAppointmentService.getPatientAppointmentsIncludingCouples(resolvedPatientId);
                    setClientAppointments(appts);
                  }
                  // Close cancel modal
                  setAppointmentToCancel(null);
                  // Show success modal
                  setShowCancelSuccess(true);
                }}
                style={{
                  background: '#dc2626',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '12px 24px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Yes, Cancel Appointment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Appointment Modal */}
      {appointmentToReschedule && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: '#16325B',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#f9fafb', fontSize: '1.5rem' }}>
              Reschedule Appointment
            </h3>
            <p style={{ color: '#d1d5db', fontSize: '16px', marginBottom: '20px' }}>
              Choose a new date and time for this appointment. The existing appointment will be moved — not cancelled.
            </p>

            {/* Current Appointment Details */}
            <div style={{
              background: '#1e3a5f',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <p style={{ color: '#d1d5db', fontSize: '12px', marginBottom: '8px' }}>Current Appointment:</p>
              <div style={{ marginBottom: '8px' }}>
                <span style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600' }}>
                  {toLocalDate(new Date(appointmentToReschedule.appointmentDate)).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              </div>
              <div>
                <span style={{ color: '#f9fafb', fontSize: '14px', fontWeight: '600' }}>
                  {formatTime12Hour(appointmentToReschedule.startTime)} - {appointmentToReschedule.modality === 'video' ? 'Video Call' : 'In-Person'}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setAppointmentToReschedule(null)}
                style={{
                  background: '#374151',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '12px 24px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Navigate to booking page with reschedule ID — appointment will be MOVED, not cancelled
                  setAppointmentToReschedule(null);
                  navigate('/book-appointment', { state: { rescheduleAppointmentId: appointmentToReschedule.id } });
                }}
                style={{
                  background: '#059669',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '12px 24px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Choose New Time
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Success Modal */}
      {showCancelSuccess && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '3rem',
                marginBottom: '1rem',
                color: '#10b981'
              }}>
                ✓
              </div>
              <h3 style={{
                color: '#1f2937',
                marginBottom: '0.5rem',
                fontSize: '1.25rem',
                fontWeight: '600'
              }}>
                Appointment Cancelled Successfully
              </h3>
              <p style={{
                color: '#d1d5db',
                marginBottom: '1.5rem',
                fontSize: '0.875rem'
              }}>
                Your provider has been notified.
              </p>
              <button
                onClick={() => setShowCancelSuccess(false)}
                style={{
                  background: '#059669',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '12px 24px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  width: '100%'
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Modal */}
      {showWelcomeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '48px 40px',
            maxWidth: '600px',
            width: '90%',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
          }}>
            <h2 style={{
              margin: '0 0 24px 0',
              color: '#1e293b',
              fontSize: '32px',
              fontWeight: '600',
              textAlign: 'left'
            }}>
              Welcome to your Client Portal
            </h2>

            <p style={{
              color: '#475569',
              fontSize: '16px',
              lineHeight: '1.6',
              marginBottom: '16px'
            }}>
              Hi {currentPatient?.firstName || 'there'},
            </p>

            <p style={{
              color: '#475569',
              fontSize: '16px',
              lineHeight: '1.6',
              marginBottom: '16px'
            }}>
              This Client Portal will help us get started by making it easy for you to review our practice policies and provide some basic information before our first session.
            </p>

            <p style={{
              color: '#475569',
              fontSize: '16px',
              lineHeight: '1.6',
              marginBottom: '32px'
            }}>
              If you leave the portal before completing everything, you can use the link we emailed to come back and start over. It should take between 5-20 minutes to finish.
            </p>

            <button
              onClick={() => {
                localStorage.setItem('hasSeenClientWelcome', 'true');
                setShowWelcomeModal(false);
                // Persist to Firestore so it survives across browsers/devices
                if (user?.uid) {
                  updateDoc(doc(db, 'users', user.uid), { hasSeenWelcome: true }).catch(() => {});
                }
              }}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#059669'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#10b981'}
            >
              GET STARTED
            </button>
          </div>
        </div>
      )}

      {/* Video Session Component - Overlays everything when active */}
      {activeVideoSession && provider && currentPatient && (
        <VideoSessionWhereby
          appointmentId={activeVideoSession.id}
          patientId={(activeVideoSession as any).patientId || currentPatient.id}
          patientName={activeVideoSession.patientName || `${currentPatient.firstName} ${currentPatient.lastName}`}
          patient2Id={(activeVideoSession as any).patient2Id}
          patient2Name={(activeVideoSession as any).patient2Name}
          providerId={provider.id}
          providerName={provider.name}
          role="client"
          currentUserId={effectivePatientId || user?.uid}
          onEndSession={handleEndVideoSession}
        />
      )}

      {/* Hope AI Companion - Floating Button */}
      {currentPatient && (
        <FloatingHopeButton
          patientId={currentPatient.id}
          patientName={`${currentPatient.firstName} ${currentPatient.lastName}`}
        />
      )}

      {/* Form Viewer Modal */}
      {activeFormAssignment && currentPatient && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '2rem',
          overflowY: 'auto'
        }}>
          <div style={{ width: '100%', maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
            <FormViewer
              assignmentId={activeFormAssignment.id}
              clientId={currentPatient.id}
              formTemplateId={activeFormAssignment.formTemplateId}
              providerId={activeFormAssignment.providerId}
              onComplete={async () => {
                setActiveFormAssignment(null);
                // Refresh form assignments
                try {
                  const updatedAssignments = await formService.getAssignmentsByClientId(currentPatient.id);
                  setAssignedForms(updatedAssignments);
                  const bookable = await formService.canClientBookAppointment(currentPatient.id);
                  setCanBookAppointment(bookable);
                } catch (err) {
                  console.error('Failed to refresh form assignments after completion:', err);
                }
              }}
              onCancel={() => setActiveFormAssignment(null)}
            />
          </div>
        </div>
      )}

      {/* Emergency Cancellation Form Modal */}
      {emergencyCancellationAppointment && currentPatient && provider && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '2rem'
        }}>
          <EmergencyCancellationForm
            appointment={{
              id: emergencyCancellationAppointment.id,
              date: emergencyCancellationAppointment.appointmentDate instanceof Date
                ? emergencyCancellationAppointment.appointmentDate.toISOString().split('T')[0]
                : String(emergencyCancellationAppointment.appointmentDate),
              time: emergencyCancellationAppointment.startTime,
              type: emergencyCancellationAppointment.modality as 'video' | 'in-person',
              providerName: emergencyCancellationAppointment.providerName || provider.name,
              providerId: emergencyCancellationAppointment.providerId
            }}
            client={{
              id: currentPatient.id,
              name: `${currentPatient.firstName} ${currentPatient.lastName}`,
              email: currentPatient.email,
              phone: currentPatient.phone
            }}
            onClose={() => setEmergencyCancellationAppointment(null)}
            onSuccess={async () => {
              // Cancel the appointment in Firestore
              if (emergencyCancellationAppointment) {
                try {
                  await firebaseAppointmentService.updateAppointment(
                    emergencyCancellationAppointment.id,
                    { status: 'cancelled' }
                  );
                  // Refresh appointments list (includes couples appointments)
                  if (resolvedPatientId) {
                    const appts = await firebaseAppointmentService.getPatientAppointmentsIncludingCouples(resolvedPatientId);
                    setClientAppointments(appts);
                  }
                } catch (err) {
                  console.error('Failed to cancel appointment after emergency cancellation:', err);
                }
              }
              setEmergencyCancellationAppointment(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ClientDashboard;
